import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../services/api";
import { useUser } from "../../context/UserContext";
import { updateFireSafetyModuleSettings } from "../../services/fireSafetyLogbookApi";
import SupplierTemplateImportPanel from "../../components/tenant/SupplierTemplateImportPanel";

const TABS = [
  { key: "overzicht", label: "Overzicht" },
  { key: "toegang", label: "Toegang" },
  { key: "scopes", label: "Scopes" },
  { key: "toegangstest", label: "Toegangstest" },
  { key: "planning", label: "Planning" },
  { key: "skills", label: "Skills" },
  { key: "facturatie", label: "Facturatie" },
  { key: "integraties", label: "Integraties" },
];

const ROLE_MATRIX_ROWS = [
  {
    domain: "Projecten lezen",
    viewer: "Ja",
    monteur: "Beperkt",
    werkvoorbereider: "Ja",
    projectleider: "Ja",
    controller: "Ja",
    tenantadmin: "Ja",
  },
  {
    domain: "Projecten wijzigen",
    viewer: "Nee",
    monteur: "Nee",
    werkvoorbereider: "Nee",
    projectleider: "Ja",
    controller: "Ja",
    tenantadmin: "Ja",
  },
  {
    domain: "Werkbonnen lezen",
    viewer: "Ja",
    monteur: "Eigen/detail",
    werkvoorbereider: "Detail",
    projectleider: "Alle",
    controller: "Alle",
    tenantadmin: "Alle",
  },
  {
    domain: "Werkbonnen plannen",
    viewer: "Nee",
    monteur: "Nee",
    werkvoorbereider: "Ja",
    projectleider: "Ja",
    controller: "Nee",
    tenantadmin: "Ja",
  },
  {
    domain: "Werkbon uitvoeren",
    viewer: "Nee",
    monteur: "Ja",
    werkvoorbereider: "Beperkt",
    projectleider: "Ja",
    controller: "Nee",
    tenantadmin: "Ja",
  },
  {
    domain: "Uren schrijven",
    viewer: "Nee",
    monteur: "Ja",
    werkvoorbereider: "Ja",
    projectleider: "Ja",
    controller: "Nee",
    tenantadmin: "Ja",
  },
  {
    domain: "Uren goedkeuren",
    viewer: "Nee",
    monteur: "Nee",
    werkvoorbereider: "Ja",
    projectleider: "Ja",
    controller: "Nee",
    tenantadmin: "Ja",
  },
  {
    domain: "Gebruikerslijst zien",
    viewer: "Nee",
    monteur: "Nee",
    werkvoorbereider: "Ja",
    projectleider: "Ja",
    controller: "Ja",
    tenantadmin: "Ja",
  },
  {
    domain: "Gebruikers direct aanmaken",
    viewer: "Nee",
    monteur: "Nee",
    werkvoorbereider: "Nee",
    projectleider: "Ja",
    controller: "Nee",
    tenantadmin: "Ja",
  },
  {
    domain: "Tenant instellingen",
    viewer: "Nee",
    monteur: "Nee",
    werkvoorbereider: "Nee",
    projectleider: "Ja",
    controller: "Nee",
    tenantadmin: "Ja",
  },
  {
    domain: "RBAC beheren",
    viewer: "Nee",
    monteur: "Nee",
    werkvoorbereider: "Nee",
    projectleider: "Nee",
    controller: "Nee",
    tenantadmin: "Ja",
  },
];

const DEBIT_WINDOW_OPTIONS = [
  { value: "start_of_month", label: "Begin van de maand" },
  { value: "mid_month", label: "Midden van de maand" },
  { value: "end_of_month", label: "Eind van de maand" },
];

const MONTEUR_MENU_SECTIONS = [
  {
    key: "werk",
    label: "Categorie Werk",
    description: "Zichtbaarheid van uitvoeringsitems in het menu.",
    items: [
      { key: "werkbonnen", label: "Werkbonnen", description: "Werkbon overzicht en detail." },
      { key: "opnames", label: "Opnames", description: "Opnamewerkbonnen en inventarisaties." },
      { key: "siteSurvey", label: "Site Survey", description: "Site survey formulieren." },
    ],
  },
  {
    key: "info",
    label: "Categorie Info",
    description: "Documentatie, meldingen en hulpmiddelen.",
    items: [
      { key: "meldingen", label: "Meldingen", description: "Persoonlijke notificaties." },
      { key: "fotos", label: "Foto's", description: "Foto-overzicht en uploads." },
      { key: "formulieren", label: "Formulieren", description: "Formulier runner en templates." },
      { key: "notities", label: "Notities", description: "Korte notities per monteur." },
      { key: "toolbox", label: "Toolbox", description: "Toolbox items en checks." },
      { key: "documenten", label: "Documenten", description: "Bestanden en bijlagen." },
    ],
  },
  {
    key: "instellingen",
    label: "Categorie Instellingen",
    description: "Persoonlijke instellingen en verlof.",
    items: [
      { key: "verlof", label: "Verlof", description: "Verlof aanvragen en status." },
      { key: "weergave", label: "Weergave", description: "UI/weergave voorkeuren." },
    ],
  },
];

const MONTEUR_MENU_PRESETS = [
  {
    key: "basis",
    label: "Basis monteur",
    description: "Focus op kernwerk en minimale info.",
    value: {
      werk: { enabled: true, items: { werkbonnen: true, opnames: false, siteSurvey: false } },
      info: {
        enabled: true,
        items: {
          meldingen: true,
          fotos: true,
          formulieren: false,
          notities: false,
          toolbox: false,
          documenten: false,
        },
      },
      instellingen: { enabled: true, items: { verlof: true, weergave: true } },
    },
  },
  {
    key: "uitgebreid",
    label: "Uitgebreid",
    description: "Alles zichtbaar voor brede inzet.",
    value: defaultMonteurMenu(),
  },
  {
    key: "lean",
    label: "Lean",
    description: "Alleen werkbonnen + meldingen.",
    value: {
      werk: { enabled: true, items: { werkbonnen: true, opnames: false, siteSurvey: false } },
      info: {
        enabled: true,
        items: {
          meldingen: true,
          fotos: false,
          formulieren: false,
          notities: false,
          toolbox: false,
          documenten: false,
        },
      },
      instellingen: { enabled: false, items: { verlof: false, weergave: false } },
    },
  },
];

export default function TenantAdminPage({ mode = "" }) {
  const { user } = useUser();
  const [activeTab, setActiveTab] = useState(() => (mode === "roles" ? "toegang" : "overzicht"));
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [actionPolicy, setActionPolicy] = useState({
    allowMonteurCreate: true,
    allowMonteurEditOwn: true,
    allowMonteurCloseOwn: true,
  });
  const [procurementPolicy, setProcurementPolicy] = useState(defaultProcurementPolicy());
  const [monteurMenu, setMonteurMenu] = useState(defaultMonteurMenu());
  const [monteurMenuPolicy, setMonteurMenuPolicy] = useState(defaultMonteurMenuPolicy());
  const [completionRequirements, setCompletionRequirements] = useState({
    requireExecutionLog: true,
    requireHours: true,
    requireMaterials: false,
    requireEvidence: true,
  });
  const [serviceShifts, setServiceShifts] = useState(defaultServiceShifts());
  const [workorderTypes, setWorkorderTypes] = useState(defaultWorkorderTypes());
  const [skillCatalogDraft, setSkillCatalogDraft] = useState({
    key: "",
    label: "",
    levelScale: "5",
  });
  const [skillCatalogPreview, setSkillCatalogPreview] = useState([
    { key: "vdsl_basic", label: "VDSL basis", levelScale: 5, active: true },
    { key: "vdsl2_bonding_specialist", label: "VDSL2 bonding specialist", levelScale: 5, active: true },
    { key: "fiber_modem_enterprise", label: "Zakelijke fiber modem", levelScale: 5, active: true },
  ]);
  const [hourTypes, setHourTypes] = useState([]);
  const [accessTestLoading, setAccessTestLoading] = useState(false);
  const [accessUsers, setAccessUsers] = useState([]);
  const [accessProjects, setAccessProjects] = useState([]);
  const [accessTasks, setAccessTasks] = useState([]);
  const [accessWerkbonnen, setAccessWerkbonnen] = useState([]);
  const [accessSelectedUserId, setAccessSelectedUserId] = useState("");
  const [accessApiLoading, setAccessApiLoading] = useState(false);
  const [accessApiResult, setAccessApiResult] = useState(null);
  const [integrationSettings, setIntegrationSettings] = useState(
    defaultIntegrationSettings()
  );
  const [scopes, setScopes] = useState([]);
  const [scopeDraft, setScopeDraft] = useState({
    type: "region",
    name: "",
    code: "",
    active: true,
  });
  const [tuPasswordInput, setTuPasswordInput] = useState("");
  const [fireSafetyModuleEnabled, setFireSafetyModuleEnabled] = useState(false);
  const [supplierTemplateLibraryEnabled, setSupplierTemplateLibraryEnabled] = useState(false);
  const [zzpModeEnabled, setZzpModeEnabled] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingSaving, setBillingSaving] = useState(false);
  const [billingError, setBillingError] = useState("");
  const [billingData, setBillingData] = useState(defaultBillingState());
  const [billingInvoices, setBillingInvoices] = useState([]);
  const [billingInvoicesConnected, setBillingInvoicesConnected] = useState(false);
  const [seatInvoices, setSeatInvoices] = useState([]);
  const [generatingSeatInvoice, setGeneratingSeatInvoice] = useState(false);
  const [payForm, setPayForm] = useState({
    amountCents: "0",
    description: "Logboek24 factuurbetaling",
  });
  const [backupStatus, setBackupStatus] = useState(defaultBackupStatus());
  const [rbacLoading, setRbacLoading] = useState(false);
  const [rbacConfig, setRbacConfig] = useState({
    permissions: [],
    roles: [],
    users: [],
  });
  const [rbacRoleForm, setRbacRoleForm] = useState(defaultRbacRoleForm());
  const [rbacEditingRoleId, setRbacEditingRoleId] = useState("");
  const [rbacPermissionSearch, setRbacPermissionSearch] = useState("");
  const [rbacExpandedPermissionGroups, setRbacExpandedPermissionGroups] = useState({});
  const [rbacUserRoleDrafts, setRbacUserRoleDrafts] = useState({});
  const [zzpOwnerTargetUserId, setZzpOwnerTargetUserId] = useState("");

  const roleNames = useMemo(() => getRoleNames(user), [user]);
  const userPermissions = useMemo(() => asArray(user?.permissions), [user]);
  const currentUserId = useMemo(
    () => String(user?.id || user?._id || user?.userId || "").trim(),
    [user]
  );
  const isPlatformAdmin =
    roleNames.includes("platformadmin") ||
    userPermissions.some((permission) => String(permission || "").trim().toLowerCase() === "platform.admin");
  const isPlatformBillingAdmin =
    isPlatformAdmin;
  const canEditMonteurMenu = isPlatformAdmin || monteurMenuPolicy.editableByTenant;
  const rbacPermissionGroups = useMemo(
    () => groupRbacPermissions(rbacConfig.permissions),
    [rbacConfig.permissions]
  );
  const filteredRbacPermissionGroups = useMemo(
    () => filterRbacPermissionGroups(rbacPermissionGroups, rbacPermissionSearch),
    [rbacPermissionGroups, rbacPermissionSearch]
  );

  const loadBilling = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setBillingLoading(true);
      setBillingError("");
      const res = await api.get("/tenant/billing");
      const normalized = normalizeBilling(res?.data?.billing);
      setBillingData(normalized);
      setPayForm((prev) => ({
        ...prev,
        amountCents: String(normalized.amountCents || 0),
      }));
    } catch (err) {
      setBillingError(err?.response?.data?.message || "Facturatie laden mislukt");
    } finally {
      if (!silent) setBillingLoading(false);
    }
  }, []);

  const loadBillingInvoices = useCallback(async () => {
    try {
      setBillingError("");
      const res = await api.get("/tenant/billing/invoices");
      const invoices = Array.isArray(res?.data?.invoices) ? res.data.invoices : [];
      const seatInvoiceItems = Array.isArray(res?.data?.seatInvoices)
        ? res.data.seatInvoices
        : [];
      setBillingInvoices(invoices);
      setBillingInvoicesConnected(Boolean(res?.data?.connected));
      setSeatInvoices(seatInvoiceItems);
    } catch (err) {
      setBillingInvoices([]);
      setSeatInvoices([]);
      setBillingInvoicesConnected(false);
      setBillingError(err?.response?.data?.message || "Facturen laden mislukt");
    }
  }, []);

  async function generateSeatInvoice() {
    try {
      setGeneratingSeatInvoice(true);
      setBillingError("");
      const res = await api.post("/tenant/billing/seat-invoices/generate");
      if (res?.data?.invoice) {
        setSeatInvoices((prev) => {
          const next = [
            res.data.invoice,
            ...prev.filter(
              (item) =>
                String(item?._id || "") !== String(res.data.invoice?._id || "")
            ),
          ];
          return next;
        });
      }
      setSuccess("Seat maandfactuur gegenereerd.");
      await loadBillingInvoices();
    } catch (err) {
      setBillingError(
        err?.response?.data?.message || "Seat maandfactuur genereren mislukt"
      );
    } finally {
      setGeneratingSeatInvoice(false);
    }
  }

  const loadBackupStatus = useCallback(async () => {
    try {
      const res = await api.get("/tenant/settings/backup-status");
      const backup = res?.data?.backup || {};
      setBackupStatus({
        updatedAt: backup.updatedAt || null,
        runStartedAt: backup.runStartedAt || null,
        runFinishedAt: backup.runFinishedAt || null,
        runStatus: String(backup.runStatus || "unknown"),
        verifyMode: String(backup.verifyMode || "fast"),
        foldersTotal: Number(backup.foldersTotal || 0),
        foldersBackedUp: Number(backup.foldersBackedUp || 0),
        verifyOk: Number(backup.verifyOk || 0),
        verifyFailed: Number(backup.verifyFailed || 0),
        lastError: String(backup.lastError || ""),
      });
    } catch {
      setBackupStatus(defaultBackupStatus());
    }
  }, []);

  const loadScopes = useCallback(async () => {
    try {
      const res = await api.get("/tenant/scopes");
      setScopes(Array.isArray(res?.data?.items) ? res.data.items : []);
    } catch {
      setScopes([]);
    }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      setSuccess("");
      const res = await api.get("/tenant/settings");
      const data = res?.data || {};

      setActionPolicy({
        allowMonteurCreate: Boolean(data.allowMonteurCreate),
        allowMonteurEditOwn: Boolean(data.allowMonteurEditOwn),
        allowMonteurCloseOwn: Boolean(data.allowMonteurCloseOwn),
      });
      setCompletionRequirements(normalizeCompletionRequirements(data.completionRequirements));
      setProcurementPolicy(normalizeProcurementPolicy(data.procurement));
      setMonteurMenu(normalizeMonteurMenu(data.monteurMenu));
      setMonteurMenuPolicy(normalizeMonteurMenuPolicy(data.monteurMenuPolicy));
      setServiceShifts(normalizeServiceShifts(data.serviceShifts));
      setWorkorderTypes(normalizeWorkorderTypes(data.workorderTypes));
      setIntegrationSettings(normalizeIntegrationSettings(data.integrations));
      setFireSafetyModuleEnabled(Boolean(data?.modules?.fireSafetyLogbook?.enabled));
      setZzpModeEnabled(Boolean(data?.modules?.zzpMode));
      setSupplierTemplateLibraryEnabled(
        Boolean(data?.modules?.supplierTemplateLibraryEnabled)
      );
      try {
        const hourTypeRes = await api.get("/tenant-hour-types");
        setHourTypes(normalizeHourTypes(hourTypeRes?.data));
      } catch {
        setHourTypes([]);
      }
      await loadBilling({ silent: true });
      setTuPasswordInput("");
      await loadScopes();
      await loadBackupStatus();
    } catch (err) {
      setError(err?.response?.data?.message || "Tenant settings laden mislukt");
    } finally {
      setLoading(false);
    }
  }, [loadBackupStatus, loadBilling, loadScopes]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const loadRbacConfigurator = useCallback(async () => {
    try {
      setRbacLoading(true);
      const res = await api.get("/rbac/configurator");
      const normalized = normalizeRbacConfiguratorData(res?.data);
      setRbacConfig(normalized);
      setRbacUserRoleDrafts(buildRbacUserRoleDrafts(normalized.users));
      setZzpOwnerTargetUserId((previous) => {
        const existingUserIds = new Set(
          (Array.isArray(normalized.users) ? normalized.users : [])
            .map((item) => String(item?._id || ""))
            .filter(Boolean)
        );
        if (previous && existingUserIds.has(previous)) return previous;
        if (currentUserId && existingUserIds.has(currentUserId)) return currentUserId;
        const firstUserId = String(normalized.users?.[0]?._id || "").trim();
        return firstUserId;
      });
      if (
        rbacEditingRoleId &&
        !normalized.roles.some((role) => String(role?._id || "") === String(rbacEditingRoleId))
      ) {
        setRbacEditingRoleId("");
        setRbacRoleForm(defaultRbacRoleForm());
      }
    } catch (err) {
      setError(err?.response?.data?.message || "RBAC configurator laden mislukt");
    } finally {
      setRbacLoading(false);
    }
  }, [currentUserId, rbacEditingRoleId]);

  function resetRbacRoleForm() {
    setRbacEditingRoleId("");
    setRbacRoleForm(defaultRbacRoleForm());
    setRbacPermissionSearch("");
    setRbacExpandedPermissionGroups({});
  }

  function startRbacRoleEdit(role) {
    const permissionKeys = Array.isArray(role?.permissions)
      ? role.permissions.map((permission) => String(permission?.key || "").trim()).filter(Boolean)
      : [];
    setRbacEditingRoleId(String(role?._id || ""));
    setRbacRoleForm({
      name: String(role?.name || ""),
      description: String(role?.description || ""),
      permissionKeys: Array.from(new Set(permissionKeys)),
    });
    setRbacPermissionSearch("");
    setRbacExpandedPermissionGroups(
      Object.fromEntries(
        rbacPermissionGroups
          .filter((group) => group.permissions.some((permission) => permissionKeys.includes(permission.key)))
          .map((group) => [group.group, true])
      )
    );
  }

  function toggleRbacRolePermission(permissionKey, checked) {
    const key = String(permissionKey || "").trim();
    if (!key) return;
    setRbacRoleForm((prev) => {
      const current = new Set(Array.isArray(prev.permissionKeys) ? prev.permissionKeys : []);
      if (checked) current.add(key);
      else current.delete(key);
      return { ...prev, permissionKeys: Array.from(current) };
    });
  }

  function toggleRbacPermissionGroup(groupName) {
    setRbacExpandedPermissionGroups((previous) => ({
      ...previous,
      [groupName]: !previous[groupName],
    }));
  }

  function setRbacPermissionGroupSelection(permissions, checked) {
    const keys = (Array.isArray(permissions) ? permissions : [])
      .map((permission) => String(permission?.key || "").trim())
      .filter(Boolean);
    if (!keys.length) return;

    setRbacRoleForm((previous) => {
      const current = new Set(Array.isArray(previous.permissionKeys) ? previous.permissionKeys : []);
      for (const key of keys) {
        if (checked) current.add(key);
        else current.delete(key);
      }
      return { ...previous, permissionKeys: Array.from(current) };
    });
  }

  async function saveRbacRole() {
    const name = String(rbacRoleForm.name || "").trim();
    if (!name) {
      setError("Rolnaam is verplicht.");
      return;
    }
    try {
      setSavingKey("rbac.role.save");
      setError("");
      setSuccess("");
      const payload = {
        name,
        description: String(rbacRoleForm.description || "").trim(),
        permissionKeys: Array.from(
          new Set(
            (Array.isArray(rbacRoleForm.permissionKeys) ? rbacRoleForm.permissionKeys : [])
              .map((item) => String(item || "").trim())
              .filter(Boolean)
          )
        ),
      };
      if (rbacEditingRoleId) {
        await api.put(`/rbac/roles/${rbacEditingRoleId}`, payload);
        setSuccess("Rol bijgewerkt.");
      } else {
        await api.post("/rbac/roles", payload);
        setSuccess("Rol aangemaakt.");
      }
      await loadRbacConfigurator();
      resetRbacRoleForm();
    } catch (err) {
      setError(err?.response?.data?.message || "Rol opslaan mislukt");
    } finally {
      setSavingKey("");
    }
  }

  async function deleteRbacRole(role) {
    const roleId = String(role?._id || "");
    const roleName = String(role?.name || "").trim() || "deze rol";
    if (!roleId) return;
    if (!window.confirm(`Weet je zeker dat je rol "${roleName}" wilt verwijderen?`)) return;
    try {
      setSavingKey(`rbac.role.delete.${roleId}`);
      setError("");
      setSuccess("");
      await api.delete(`/rbac/roles/${roleId}`);
      await loadRbacConfigurator();
      if (String(rbacEditingRoleId) === roleId) {
        resetRbacRoleForm();
      }
      setSuccess("Rol verwijderd.");
    } catch (err) {
      setError(err?.response?.data?.message || "Rol verwijderen mislukt");
    } finally {
      setSavingKey("");
    }
  }

  function toggleRbacUserRoleDraft(userId, roleId, checked) {
    const userKey = String(userId || "");
    const roleKey = String(roleId || "");
    if (!userKey || !roleKey) return;
    setRbacUserRoleDrafts((prev) => {
      const current = new Set(Array.isArray(prev[userKey]) ? prev[userKey] : []);
      if (checked) current.add(roleKey);
      else current.delete(roleKey);
      return { ...prev, [userKey]: Array.from(current) };
    });
  }

  async function saveRbacUserRoles(userId) {
    const userKey = String(userId || "");
    if (!userKey) return;
    try {
      setSavingKey(`rbac.userroles.save.${userKey}`);
      setError("");
      setSuccess("");
      const roleIds = Array.isArray(rbacUserRoleDrafts[userKey])
        ? rbacUserRoleDrafts[userKey]
        : [];
      await api.put(`/rbac/users/${userKey}/roles`, { roleIds });
      await loadRbacConfigurator();
      setSuccess("Gebruikersrollen bijgewerkt.");
    } catch (err) {
      setError(err?.response?.data?.message || "Gebruikersrollen opslaan mislukt");
    } finally {
      setSavingKey("");
    }
  }

  async function activateZzpOwnerPreset() {
    if (!zzpModeEnabled) {
      setError("ZZP-modus staat uit. Zet ZZP-modus eerst aan.");
      return;
    }

    const targetUserId = String(zzpOwnerTargetUserId || currentUserId || "").trim();
    if (!targetUserId) {
      setError("Kies eerst een gebruiker voor de ZZP eigenaar preset.");
      return;
    }

    try {
      setSavingKey("zzpOwnerPreset.activate");
      setError("");
      setSuccess("");
      const res = await api.post("/tenant/settings/zzp-owner-preset/activate", {
        userId: targetUserId,
        replaceRoles: true,
      });
      await loadRbacConfigurator();
      setSuccess(res?.data?.message || "ZZP eigenaar preset geactiveerd.");
    } catch (err) {
      setError(err?.response?.data?.message || "ZZP eigenaar preset activeren mislukt");
    } finally {
      setSavingKey("");
    }
  }

  async function createScope() {
    const name = String(scopeDraft.name || "").trim();
    if (!name) {
      setError("Scope naam is verplicht.");
      return;
    }
    try {
      setSavingKey("scope.create");
      setError("");
      setSuccess("");
      await api.post("/tenant/scopes", {
        type: String(scopeDraft.type || "region").trim().toLowerCase(),
        name,
        code: String(scopeDraft.code || "").trim(),
        active: scopeDraft.active !== false,
      });
      setScopeDraft({ type: "region", name: "", code: "", active: true });
      await loadScopes();
      setSuccess("Scope aangemaakt.");
    } catch (err) {
      setError(err?.response?.data?.message || "Scope aanmaken mislukt");
    } finally {
      setSavingKey("");
    }
  }

  async function toggleScopeActive(scope) {
    try {
      setSavingKey(`scope.toggle.${scope._id}`);
      setError("");
      setSuccess("");
      await api.put(`/tenant/scopes/${scope._id}`, { active: !(scope.active !== false) });
      await loadScopes();
      setSuccess("Scope bijgewerkt.");
    } catch (err) {
      setError(err?.response?.data?.message || "Scope bijwerken mislukt");
    } finally {
      setSavingKey("");
    }
  }

  async function deleteScope(scope) {
    const name = String(scope?.name || "").trim() || "deze scope";
    if (!window.confirm(`Weet je zeker dat je ${name} wilt verwijderen?`)) return;
    try {
      setSavingKey(`scope.delete.${scope._id}`);
      setError("");
      setSuccess("");
      await api.delete(`/tenant/scopes/${scope._id}`);
      await loadScopes();
      setSuccess("Scope verwijderd.");
    } catch (err) {
      setError(err?.response?.data?.message || "Scope verwijderen mislukt");
    } finally {
      setSavingKey("");
    }
  }

  async function updateActionPolicy(key, value) {
    const previous = { ...actionPolicy };
    setActionPolicy((prev) => ({ ...prev, [key]: value }));
    setSavingKey(key);
    setError("");
    setSuccess("");

    try {
      await api.put("/tenant/settings/action-policies", { [key]: value });
      setSuccess("Actie-policy bijgewerkt.");
    } catch (err) {
      setActionPolicy(previous);
      setError(err?.response?.data?.message || "Actie-policy opslaan mislukt");
    } finally {
      setSavingKey("");
    }
  }

  async function saveProcurementPolicy() {
    setSavingKey("procurementPolicy");
    setError("");
    setSuccess("");

    const roles = String(procurementPolicy.approvalRolesText || "")
      .split(",")
      .map((item) => String(item || "").trim().toLowerCase())
      .filter(Boolean);

    if (!roles.length) {
      setSavingKey("");
      setError("Vul minimaal 1 approver rol in.");
      return;
    }

    try {
      const payload = {
        allowUnlinkedOrders: Boolean(procurementPolicy.allowUnlinkedOrders),
        requireApprovalAbove: Number(procurementPolicy.requireApprovalAbove || 0),
        technicianOrderLimit: Number(procurementPolicy.technicianOrderLimit || 0),
        approvalRoles: Array.from(new Set(roles)),
      };
      const res = await api.put("/tenant/settings/procurement-policy", payload);
      setProcurementPolicy(normalizeProcurementPolicy(res?.data?.procurement));
      setSuccess("Inkoopbeleid bijgewerkt.");
    } catch (err) {
      setError(err?.response?.data?.message || "Inkoopbeleid opslaan mislukt");
    } finally {
      setSavingKey("");
    }
  }

  function updateMonteurMenuCategory(categoryKey, enabled) {
    setMonteurMenu((prev) => ({
      ...prev,
      [categoryKey]: {
        ...(prev[categoryKey] || {}),
        enabled,
      },
    }));
  }

  function updateMonteurMenuItem(categoryKey, itemKey, enabled) {
    setMonteurMenu((prev) => ({
      ...prev,
      [categoryKey]: {
        ...(prev[categoryKey] || {}),
        items: {
          ...((prev[categoryKey] || {}).items || {}),
          [itemKey]: enabled,
        },
      },
    }));
  }

  async function saveMonteurMenu() {
    if (!canEditMonteurMenu) {
      setError("Monteur menu is nog niet vrijgegeven door Platform Admin.");
      setSuccess("");
      return;
    }

    setSavingKey("monteurMenu");
    setError("");
    setSuccess("");

    try {
      const payload = buildMonteurMenuPayload(monteurMenu);
      const res = await api.put("/tenant/settings/monteur-menu", payload);
      setMonteurMenu(normalizeMonteurMenu(res?.data?.monteurMenu));
      setSuccess("Monteur menu bijgewerkt.");
    } catch (err) {
      setError(err?.response?.data?.message || "Monteur menu opslaan mislukt");
    } finally {
      setSavingKey("");
    }
  }

  async function updateMonteurMenuPolicy(editableByTenant) {
    if (!isPlatformAdmin) return;
    const previous = monteurMenuPolicy;
    setMonteurMenuPolicy({ editableByTenant });
    setSavingKey("monteurMenuPolicy");
    setError("");
    setSuccess("");

    try {
      const res = await api.put("/tenant/settings/monteur-menu-policy", {
        editableByTenant,
      });
      setMonteurMenuPolicy(normalizeMonteurMenuPolicy(res?.data?.monteurMenuPolicy));
      setSuccess(
        editableByTenant
          ? "Monteur menu vrijgegeven voor tenant admin."
          : "Monteur menu vergrendeld voor tenant admin."
      );
    } catch (err) {
      setMonteurMenuPolicy(previous);
      setError(err?.response?.data?.message || "Monteur menu policy opslaan mislukt");
    } finally {
      setSavingKey("");
    }
  }

  function applyMonteurMenuPreset(presetKey) {
    const preset = MONTEUR_MENU_PRESETS.find((item) => item.key === presetKey);
    if (!preset) return;
    setMonteurMenu(normalizeMonteurMenu(preset.value));
    setSuccess(`Preset toegepast: ${preset.label}. Klik nog op "Monteur menu opslaan".`);
    setError("");
  }

  function resetMonteurMenuCategory(categoryKey) {
    const defaults = defaultMonteurMenu();
    if (!defaults[categoryKey]) return;
    setMonteurMenu((prev) => ({
      ...prev,
      [categoryKey]: defaults[categoryKey],
    }));
    const section = MONTEUR_MENU_SECTIONS.find((item) => item.key === categoryKey);
    setSuccess(
      `Categorie teruggezet: ${section?.label || categoryKey}. Klik nog op "Monteur menu opslaan".`
    );
    setError("");
  }

  async function updateCompletionRequirement(key, value) {
    const previous = { ...completionRequirements };
    setCompletionRequirements((prev) => ({ ...prev, [key]: value }));
    setSavingKey(key);
    setError("");
    setSuccess("");

    try {
      await api.put("/tenant/settings/completion-requirements", { [key]: value });
      setSuccess("Afrondvereiste bijgewerkt.");
    } catch (err) {
      setCompletionRequirements(previous);
      setError(err?.response?.data?.message || "Afrondvereiste opslaan mislukt");
    } finally {
      setSavingKey("");
    }
  }

  async function updateIntegrationSetting(moduleKey, field, value) {
    const previous = integrationSettings;
    const next = {
      ...integrationSettings,
      [moduleKey]: {
        ...(integrationSettings[moduleKey] || {}),
        [field]: value,
      },
    };

    setIntegrationSettings(next);
    setSavingKey(`${moduleKey}.${field}`);
    setError("");
    setSuccess("");

    try {
      const payload = { [moduleKey]: { [field]: value } };
      const res = await api.put("/tenant/settings/integrations", payload);
      setIntegrationSettings(normalizeIntegrationSettings(res?.data?.integrations));
      setSuccess("Integratie-instelling bijgewerkt.");
    } catch (err) {
      setIntegrationSettings(previous);
      setError(
        err?.response?.data?.message || "Integratie-instelling opslaan mislukt"
      );
    } finally {
      setSavingKey("");
    }
  }

  async function saveTuConfig() {
    setSavingKey("tu.config");
    setError("");
    setSuccess("");

    const payload = {
      tu: {
        baseUrl: integrationSettings.tu.baseUrl,
        username: integrationSettings.tu.username,
        customerNumber: integrationSettings.tu.customerNumber,
        hookUrl: integrationSettings.tu.hookUrl,
        vendor: integrationSettings.tu.vendor,
        unitType: integrationSettings.tu.unitType,
        includeGrossPrice: integrationSettings.tu.includeGrossPrice,
        includeDisposalContribution:
          integrationSettings.tu.includeDisposalContribution,
        includeGtin: integrationSettings.tu.includeGtin,
        includeVatPct: integrationSettings.tu.includeVatPct,
        includeLeadTime: integrationSettings.tu.includeLeadTime,
      },
    };

    if (tuPasswordInput.trim()) {
      payload.tu.password = tuPasswordInput;
    }

    try {
      const res = await api.put("/tenant/settings/integrations", payload);
      setIntegrationSettings(normalizeIntegrationSettings(res?.data?.integrations));
      setTuPasswordInput("");
      setSuccess("TU configuratie opgeslagen.");
    } catch (err) {
      setError(err?.response?.data?.message || "TU configuratie opslaan mislukt");
    } finally {
      setSavingKey("");
    }
  }

  async function saveBillingSettings() {
    try {
      setBillingSaving(true);
      setBillingError("");
      const payload = {
        preferredDebitWindow: String(billingData.preferredDebitWindow || "start_of_month"),
      };

      if (isPlatformBillingAdmin) {
        payload.planCode = String(billingData.planCode || "basis").trim() || "basis";
        payload.amountCents = Number.parseInt(String(billingData.amountCents || 0), 10) || 0;
        payload.maxAttempts = Number.parseInt(String(billingData.maxAttempts || 2), 10) || 2;
        payload.retryIntervalDays = Number.parseInt(String(billingData.retryIntervalDays || 7), 10) || 7;
      }
      const res = await api.patch("/tenant/billing/settings", payload);
      const normalized = normalizeBilling(res?.data?.billing);
      setBillingData(normalized);
      setPayForm((prev) => ({
        ...prev,
        amountCents: String(normalized.amountCents || 0),
      }));
      setSuccess("Facturatie-instellingen opgeslagen.");
    } catch (err) {
      setBillingError(err?.response?.data?.message || "Facturatie-instellingen opslaan mislukt");
    } finally {
      setBillingSaving(false);
    }
  }

  async function createPayLink() {
    try {
      setBillingSaving(true);
      setBillingError("");
      const payload = {
        amountCents: Number.parseInt(String(payForm.amountCents || "0"), 10) || 0,
        description: String(payForm.description || "Logboek24 factuurbetaling").trim(),
      };
      const res = await api.post("/tenant/billing/pay-link", payload);
      const checkoutUrl = String(res?.data?.checkoutUrl || "").trim();
      if (checkoutUrl) {
        window.open(checkoutUrl, "_blank", "noopener,noreferrer");
      }
      setSuccess("Betaallink aangemaakt.");
      await loadBilling({ silent: true });
    } catch (err) {
      setBillingError(err?.response?.data?.message || "Betaallink maken mislukt");
    } finally {
      setBillingSaving(false);
    }
  }

  async function retryBillingNow() {
    try {
      setBillingSaving(true);
      setBillingError("");
      const res = await api.post("/tenant/billing/retry-now");
      const checkoutUrl = String(res?.data?.checkoutUrl || "").trim();
      if (checkoutUrl) {
        window.open(checkoutUrl, "_blank", "noopener,noreferrer");
      }
      setSuccess("Herincasso gestart.");
      await loadBilling({ silent: true });
    } catch (err) {
      setBillingError(err?.response?.data?.message || "Herincasso starten mislukt");
    } finally {
      setBillingSaving(false);
    }
  }

  async function restoreBillingAccess() {
    try {
      setBillingSaving(true);
      setBillingError("");
      const res = await api.post("/tenant/billing/restore-access");
      setBillingData(normalizeBilling(res?.data?.billing));
      setSuccess("Toegang hersteld.");
    } catch (err) {
      setBillingError(err?.response?.data?.message || "Toegang herstellen mislukt");
    } finally {
      setBillingSaving(false);
    }
  }

  async function toggleFireSafetyModule(nextValue) {
    const previous = fireSafetyModuleEnabled;
    setFireSafetyModuleEnabled(nextValue);
    setSavingKey("fireSafetyLogbook.enabled");
    setError("");
    setSuccess("");

    try {
      const state = await updateFireSafetyModuleSettings(nextValue);
      setFireSafetyModuleEnabled(Boolean(state.enabled));
      setSuccess("Installaties & logboek module bijgewerkt.");
    } catch (err) {
      setFireSafetyModuleEnabled(previous);
      setError(err?.response?.data?.message || "Module-instelling opslaan mislukt");
    } finally {
      setSavingKey("");
    }
  }

  async function toggleSupplierTemplateLibrary(nextValue) {
    const previous = supplierTemplateLibraryEnabled;
    setSupplierTemplateLibraryEnabled(nextValue);
    setSavingKey("supplierTemplateLibrary.enabled");
    setError("");
    setSuccess("");

    try {
      const res = await api.put("/tenant/settings/modules", {
        supplierTemplateLibraryEnabled: Boolean(nextValue),
      });
      const enabled = Boolean(res?.data?.modules?.supplierTemplateLibraryEnabled);
      setSupplierTemplateLibraryEnabled(enabled);
      setSuccess(
        enabled
          ? "Standaard leveranciersbibliotheek ingeschakeld."
          : "Standaard leveranciersbibliotheek uitgeschakeld."
      );
    } catch (err) {
      setSupplierTemplateLibraryEnabled(previous);
      setError(err?.response?.data?.message || "Module-instelling opslaan mislukt");
    } finally {
      setSavingKey("");
    }
  }

  async function toggleZzpMode(nextValue) {
    const previous = zzpModeEnabled;
    setZzpModeEnabled(nextValue);
    setSavingKey("zzpMode.enabled");
    setError("");
    setSuccess("");

    try {
      const res = await api.put("/tenant/settings/modules", {
        zzpMode: Boolean(nextValue),
      });
      const enabled = Boolean(res?.data?.modules?.zzpMode);
      setZzpModeEnabled(enabled);
      setSuccess(
        enabled
          ? "ZZP-modus ingeschakeld. Monteur kan klant-werkbonnen zonder project aanmaken."
          : "ZZP-modus uitgeschakeld. Monteur-flow volgt weer verplichte projectkoppeling."
      );
    } catch (err) {
      setZzpModeEnabled(previous);
      setError(err?.response?.data?.message || "ZZP-modus opslaan mislukt");
    } finally {
      setSavingKey("");
    }
  }

  const loadAccessTestData = useCallback(async () => {
    try {
      setAccessTestLoading(true);
      setError("");
      const [usersRes, projectsRes, tasksRes, werkbonnenRes] = await Promise.allSettled([
        api.get("/users"),
        api.get("/projects"),
        api.get("/tasks"),
        api.get("/werkbonnen"),
      ]);

      const users = usersRes.status === "fulfilled"
        && Array.isArray(usersRes.value?.data?.users)
        ? usersRes.value.data.users
        : [];
      const projects = projectsRes.status === "fulfilled" && Array.isArray(projectsRes.value?.data)
        ? projectsRes.value.data
        : [];
      const tasks = tasksRes.status === "fulfilled" && Array.isArray(tasksRes.value?.data)
        ? tasksRes.value.data
        : [];
      const werkbonnen = werkbonnenRes.status === "fulfilled" && Array.isArray(werkbonnenRes.value?.data)
        ? werkbonnenRes.value.data
        : [];

      setAccessUsers(users);
      setAccessProjects(projects);
      setAccessTasks(tasks);
      setAccessWerkbonnen(werkbonnen);

      if (!accessSelectedUserId && users.length > 0) {
        setAccessSelectedUserId(String(users[0]._id));
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Toegangstest data laden mislukt");
    } finally {
      setAccessTestLoading(false);
    }
  }, [accessSelectedUserId]);

  useEffect(() => {
    if (activeTab === "toegangstest") {
      loadAccessTestData();
    }
  }, [activeTab, loadAccessTestData]);

  useEffect(() => {
    if (activeTab === "facturatie") {
      loadBilling();
      loadBillingInvoices();
    }
  }, [activeTab, loadBilling, loadBillingInvoices]);

  useEffect(() => {
    if (activeTab === "toegang") {
      loadRbacConfigurator();
    }
  }, [activeTab, loadRbacConfigurator]);

  async function runAccessApiTest() {
    if (!accessSelectedUserId) return;
    try {
      setAccessApiLoading(true);
      setError("");
      const res = await api.get(`/users/access-test/${accessSelectedUserId}`);
      setAccessApiResult(res?.data || null);
    } catch (err) {
      setAccessApiResult(null);
      setError(err?.response?.data?.message || "API toegangstest mislukt");
    } finally {
      setAccessApiLoading(false);
    }
  }

  const moneybirdState = integrationSettings.moneybird;
  const tuState = integrationSettings.tu;
  const subscriptionStatusLabel = formatSubscriptionStatus(billingData.subscriptionStatus);
  const paymentStatusLabel = formatPaymentStatus(billingData.paymentStatus);
  const selectedAccessUser = accessUsers.find(
    (item) => String(item._id) === String(accessSelectedUserId)
  );
  const accessRoleNames = getRoleNames(selectedAccessUser);
  const isSubcontractor =
    accessRoleNames.includes("onderaannemer") || accessRoleNames.includes("subcontractor");

  const memberProjectIds = isSubcontractor
    ? accessProjects
        .filter((project) =>
          Array.isArray(project.members) &&
          project.members.some(
            (member) =>
              String(member?.userId?._id || member?.userId || "") ===
              String(selectedAccessUser?._id || "")
          )
        )
        .map((project) => String(project._id))
    : accessProjects.map((project) => String(project._id));

  const visibleProjects = accessProjects.filter((project) =>
    memberProjectIds.includes(String(project._id))
  );
  const visibleTasks = accessTasks.filter((task) => {
    if (!isSubcontractor) return true;
    const assignedToUser =
      String(task?.assignedTo?._id || task?.assignedTo || "") ===
      String(selectedAccessUser?._id || "");
    const onMemberProject = memberProjectIds.includes(
      String(task?.projectId?._id || task?.projectId || "")
    );
    return assignedToUser || onMemberProject;
  });
  const visibleWerkbonnen = accessWerkbonnen.filter((werkbon) => {
    if (!isSubcontractor) return true;
    const assignedToUser =
      String(werkbon?.assignedTo?._id || werkbon?.assignedTo || "") ===
      String(selectedAccessUser?._id || "");
    const inTeam =
      Array.isArray(werkbon?.team) &&
      werkbon.team.some(
        (member) =>
          String(member?.userId?._id || member?.userId || "") ===
          String(selectedAccessUser?._id || "")
      );
    return assignedToUser || inTeam;
  });
  const activeWorkorderTypeCount = workorderTypes.filter((item) => item.active !== false).length;
  const skillWorkorderPreviewRows = useMemo(
    () =>
      workorderTypes.slice(0, 8).map((type) => ({
        key: type.key || "",
        name: type.name || type.key || "-",
        mode: type.key?.includes("storing") ? "warn" : "advice",
        requiredSkills:
          type.key?.includes("storing") && type.key?.includes("zakelijk")
            ? "vdsl2_bonding_specialist>=3"
            : type.key?.includes("storing")
            ? "vdsl_basic>=1"
            : "",
      })),
    [workorderTypes]
  );

  const leakedTasks = isSubcontractor
    ? accessTasks.filter((task) => {
        const assignedToUser =
          String(task?.assignedTo?._id || task?.assignedTo || "") ===
          String(selectedAccessUser?._id || "");
        const onMemberProject = memberProjectIds.includes(
          String(task?.projectId?._id || task?.projectId || "")
        );
        return !assignedToUser && !onMemberProject;
      })
    : [];

  function updateServiceShift(index, field, value) {
    setServiceShifts((prev) =>
      prev.map((item, currentIndex) =>
        currentIndex === index ? { ...item, [field]: value } : item
      )
    );
  }

  function addServiceShift() {
    setServiceShifts((prev) => [
      ...prev,
      {
        key: `dienst-${prev.length + 1}`,
        label: "",
        color: "#0f172a",
        startTime: "08:00",
        endTime: "16:00",
        active: true,
      },
    ]);
  }

  function removeServiceShift(index) {
    setServiceShifts((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
  }

  async function saveServiceShifts() {
    setSavingKey("serviceShifts");
    setError("");
    setSuccess("");

    try {
      const payload = {
        serviceShifts: serviceShifts.map((item, index) => ({
          key:
            String(item.key || item.label || `dienst-${index + 1}`)
              .trim()
              .toLowerCase()
              .replace(/\s+/g, "-") || `dienst-${index + 1}`,
          label: String(item.label || "").trim(),
          color: String(item.color || "#0f172a").trim() || "#0f172a",
          startTime: String(item.startTime || "").trim(),
          endTime: String(item.endTime || "").trim(),
          active: item.active !== false,
        })),
      };
      const res = await api.put("/tenant/settings/service-shifts", payload);
      setServiceShifts(normalizeServiceShifts(res?.data?.serviceShifts));
      setSuccess("Diensttypes opgeslagen.");
    } catch (err) {
      setError(err?.response?.data?.message || "Diensttypes opslaan mislukt");
    } finally {
      setSavingKey("");
    }
  }

  function updateWorkorderType(index, field, value) {
    setWorkorderTypes((prev) =>
      prev.map((item, currentIndex) =>
        currentIndex === index ? { ...item, [field]: value } : item
      )
    );
  }

  function updateWorkorderTypeUiRole(index, roleKey, field, value) {
    setWorkorderTypes((prev) =>
      prev.map((item, currentIndex) => {
        if (currentIndex !== index) return item;
        const nextUiPolicy = normalizeWorkorderUiPolicy(item?.uiPolicy, {
          workorderKey: item?.key || item?.systemKey || item?.name || `type_${index + 1}`,
          signatureRequired: Boolean(item?.signatureRequired),
        });
        return {
          ...item,
          uiPolicy: {
            roles: {
              ...nextUiPolicy.roles,
              [roleKey]: {
                ...nextUiPolicy.roles[roleKey],
                [field]: value,
              },
            },
          },
        };
      })
    );
  }

  function applyWorkorderTypeUiPreset(index, presetKey) {
    if (!presetKey || presetKey === "custom") return;
    setWorkorderTypes((prev) =>
      prev.map((item, currentIndex) => {
        if (currentIndex !== index) return item;
        const workorderKey =
          item?.key || item?.systemKey || item?.name || `type_${index + 1}`;
        return {
          ...item,
          uiPolicy: createWorkorderUiPreset(presetKey, {
            workorderKey,
            signatureRequired: Boolean(item?.signatureRequired),
          }),
        };
      })
    );
  }

  function addWorkorderType() {
    setWorkorderTypes((prev) => [
      ...prev,
      {
        key: `type_${prev.length + 1}`,
        name: "",
        description: "",
        purpose: "",
        origin: "tenant",
        systemKey: "",
        sortOrder: (prev.length + 1) * 10,
        active: true,
        signatureRequired: false,
        generateWorkReport: true,
        moneybirdSync: false,
        publishToClientHub: false,
        allowNacalculatie: true,
        requireHours: true,
        defaultHourTypeId: "",
        allowedHourTypeIds: [],
        workflow: {
          requireHours: true,
        },
        uiPolicy: normalizeWorkorderUiPolicy(
          {},
          { workorderKey: `type_${prev.length + 1}`, signatureRequired: false }
        ),
      },
    ]);
  }

  function removeWorkorderType(index) {
    setWorkorderTypes((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
  }

  async function saveWorkorderTypes() {
    setSavingKey("workorderTypes");
    setError("");
    setSuccess("");
    if (workorderTypes.filter((item) => item.active !== false).length === 0) {
      setSavingKey("");
      setError("Minimaal 1 actief werkbontype is verplicht.");
      return;
    }

    try {
      const payload = {
        workorderTypes: workorderTypes.map((item, index) => ({
          key:
            String(item.key || item.name || `type_${index + 1}`)
              .trim()
              .toLowerCase()
              .replace(/\s+/g, "_") || `type_${index + 1}`,
          name: String(item.name || "").trim(),
          description: String(item.description || "").trim(),
          purpose: String(item.purpose || "").trim(),
          origin: String(item.origin || "").trim().toLowerCase() === "system" ? "system" : "tenant",
          systemKey: String(item.systemKey || "").trim(),
          sortOrder: Number.isFinite(Number(item.sortOrder)) ? Number(item.sortOrder) : (index + 1) * 10,
          active: item.active !== false,
          signatureRequired: Boolean(item.signatureRequired),
          generateWorkReport:
            item.generateWorkReport === undefined ? true : Boolean(item.generateWorkReport),
          moneybirdSync: Boolean(item.moneybirdSync),
          publishToClientHub: Boolean(item.publishToClientHub),
          allowNacalculatie:
            item.allowNacalculatie === undefined ? true : Boolean(item.allowNacalculatie),
          requireHours: item.requireHours === undefined ? true : Boolean(item.requireHours),
          defaultHourTypeId: String(item.defaultHourTypeId || "").trim(),
          // Nog licht gebruik: backend voorbereid, UI nog niet dwingend.
          allowedHourTypeIds: Array.isArray(item.allowedHourTypeIds)
            ? item.allowedHourTypeIds.map((id) => String(id || "").trim()).filter(Boolean)
            : [],
          workflow: {
            ...(item?.workflow && typeof item.workflow === "object" ? item.workflow : {}),
            requireHours: item.requireHours === undefined ? true : Boolean(item.requireHours),
          },
          uiPolicy: normalizeWorkorderUiPolicy(item?.uiPolicy, {
            workorderKey:
              String(item.key || item.name || `type_${index + 1}`)
                .trim()
                .toLowerCase()
                .replace(/\s+/g, "_") || `type_${index + 1}`,
            signatureRequired: Boolean(item.signatureRequired),
          }),
        })),
      };
      const res = await api.put("/tenant/settings/workorder-types", payload);
      setWorkorderTypes(normalizeWorkorderTypes(res?.data?.workorderTypes));
      setSuccess("Werkbontypes opgeslagen.");
    } catch (err) {
      setError(err?.response?.data?.message || "Werkbontypes opslaan mislukt");
    } finally {
      setSavingKey("");
    }
  }

  function addSkillPreview() {
    const key = String(skillCatalogDraft.key || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");
    const label = String(skillCatalogDraft.label || "").trim();
    const levelScale = Number(skillCatalogDraft.levelScale || 5);

    if (!key || !label) {
      setError("Vul skill key en label in.");
      return;
    }

    if (skillCatalogPreview.some((item) => item.key === key)) {
      setError("Skill key bestaat al in deze preview.");
      return;
    }

    setError("");
    setSuccess("Skill toegevoegd aan preview (nog niet opgeslagen in backend).");
    setSkillCatalogPreview((prev) => [
      ...prev,
      { key, label, levelScale: Number.isFinite(levelScale) ? levelScale : 5, active: true },
    ]);
    setSkillCatalogDraft({ key: "", label: "", levelScale: "5" });
  }

  return (
    <div className="space-y-6">
      <header className="bg-white rounded-xl shadow p-5">
        <h2 className="text-2xl font-bold">Tenant Admin</h2>
        <p className="text-sm text-gray-600 mt-1">
          Tenant-instellingen voor modules, koppelingen en toegangsbeleid.
        </p>
      </header>

      <section className="bg-white rounded-xl shadow p-3">
        <div className="flex flex-wrap gap-2">
          {TABS.map((tab) => {
            const selected = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 rounded-lg text-sm border transition ${
                  selected
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
          <button
            onClick={loadSettings}
            className="ml-auto px-4 py-2 rounded-lg text-sm border border-slate-300 hover:bg-slate-50"
          >
            Vernieuwen
          </button>
        </div>
      </section>

      {loading && (
        <section className="bg-white rounded-xl shadow p-5">
          <p className="text-sm text-gray-500">Laden...</p>
        </section>
      )}

      {error && (
        <section className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-sm text-red-700">{error}</p>
        </section>
      )}

      {success && (
        <section className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <p className="text-sm text-emerald-700">{success}</p>
        </section>
      )}

      {!loading && activeTab === "overzicht" && (
        <div className="grid gap-4 lg:grid-cols-2">
          <InfoCard
            title="Tenant context"
            rows={[
              { label: "Tenant ID", value: String(user?.tenantId || "-") },
              { label: "Ingelogde gebruiker", value: String(user?.naam || user?.email || "-") },
              { label: "Rollen", value: roleNames.join(", ") || "-" },
              { label: "Permissions in token", value: String(userPermissions.length) },
            ]}
          />

          <InfoCard
            title="Module status"
            rows={[
              {
                label: "Installaties & logboek",
                value: fireSafetyModuleEnabled ? "Ingeschakeld" : "Uitgeschakeld",
              },
              {
                label: "Moneybird",
                value: moneybirdState.enabled
                  ? moneybirdState.connected
                    ? "Actief + verbonden"
                    : "Actief, nog niet verbonden"
                  : "Uitgeschakeld",
              },
              {
                label: "Technische Unie",
                value: tuState.enabled
                  ? tuState.connected
                    ? "Actief + verbonden"
                    : "Actief, nog niet verbonden"
                  : "Uitgeschakeld",
              },
              {
                label: "Supplier templates",
                value: supplierTemplateLibraryEnabled ? "Ingeschakeld" : "Uitgeschakeld",
              },
              {
                label: "ZZP-modus",
                value: zzpModeEnabled ? "Ingeschakeld" : "Uitgeschakeld",
              },
            ]}
          />

          <InfoCard
            title="Backup status"
            rows={[
              { label: "Laatste update", value: formatDateTime(backupStatus.updatedAt) },
              { label: "Run status", value: formatBackupRunStatus(backupStatus.runStatus) },
              { label: "Controle modus", value: backupStatus.verifyMode === "deep" ? "Diep (checksum)" : "Snel" },
              { label: "Mappen geback-upt", value: `${backupStatus.foldersBackedUp}/${backupStatus.foldersTotal}` },
              { label: "Verify OK/Fout", value: `${backupStatus.verifyOk}/${backupStatus.verifyFailed}` },
              { label: "Laatste fout", value: backupStatus.lastError || "-" },
            ]}
          />

          <section className="bg-white rounded-xl shadow p-5 lg:col-span-2">
            <h3 className="text-lg font-semibold mb-2">Scaffold roadmap</h3>
            <ul className="text-sm text-gray-700 space-y-1 list-disc pl-5">
              <li>Rollen en rechten editor (RBAC v2) komt hier als volgende stap.</li>
              <li>Object-ID beheer voor externe systemen komt als aparte sectie.</li>
              <li>Audit en change history voor tenant-settings volgt daarna.</li>
            </ul>
          </section>
        </div>
      )}

      {!loading && activeTab === "toegang" && (
        <div className="space-y-4">
          <section className="bg-white rounded-xl shadow p-5 space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold">RBAC v2 configurator</h3>
                <p className="text-sm text-gray-600">
                  Beheer tenant-rollen, permissies en gebruikerstoewijzingen.
                </p>
              </div>
              <button
                type="button"
                onClick={loadRbacConfigurator}
                disabled={rbacLoading}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                {rbacLoading ? "Laden..." : "RBAC verversen"}
              </button>
            </div>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-3">
              <div>
                <p className="text-sm font-semibold text-amber-900">ZZP eigenaar preset</p>
                <p className="text-xs text-amber-800">
                  Activeert rolbundel <code>zzp_owner</code> en zet deze als enige rol op de gekozen gebruiker.
                </p>
              </div>
              <div className="flex flex-wrap items-end gap-2">
                <label className="min-w-[240px] text-xs text-amber-900">
                  Gebruiker
                  <select
                    value={zzpOwnerTargetUserId}
                    onChange={(event) => setZzpOwnerTargetUserId(event.target.value)}
                    className="mt-1 w-full rounded border border-amber-300 bg-white px-2 py-2 text-xs text-slate-800"
                  >
                    {(Array.isArray(rbacConfig.users) ? rbacConfig.users : []).map((rbacUser) => {
                      const userId = String(rbacUser?._id || "");
                      return (
                        <option key={userId} value={userId}>
                          {(rbacUser?.naam || rbacUser?.email || userId) + (rbacUser?.email ? ` (${rbacUser.email})` : "")}
                        </option>
                      );
                    })}
                  </select>
                </label>
                <button
                  type="button"
                  onClick={activateZzpOwnerPreset}
                  disabled={
                    savingKey === "zzpOwnerPreset.activate" ||
                    !zzpModeEnabled ||
                    !zzpOwnerTargetUserId
                  }
                  className="rounded-lg bg-amber-600 px-3 py-2 text-xs text-white hover:bg-amber-700 disabled:opacity-60"
                >
                  {savingKey === "zzpOwnerPreset.activate"
                    ? "Activeren..."
                    : "Activeer ZZP eigenaar preset"}
                </button>
              </div>
              {!zzpModeEnabled ? (
                <p className="text-xs text-amber-800">
                  ZZP-modus staat uit. Zet deze eerst aan op tab Integraties.
                </p>
              ) : null}
            </div>

            <div className="grid gap-4 xl:grid-cols-2">
              <div className="rounded-lg border p-4 space-y-3">
                <h4 className="text-sm font-semibold text-slate-800">
                  {rbacEditingRoleId ? "Rol bewerken" : "Nieuwe rol"}
                </h4>
                <label className="block text-xs text-slate-600">
                  Naam
                  <input
                    type="text"
                    value={rbacRoleForm.name}
                    onChange={(event) =>
                      setRbacRoleForm((prev) => ({ ...prev, name: event.target.value }))
                    }
                    className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
                    placeholder="Bijv. Planner"
                  />
                </label>
                <label className="block text-xs text-slate-600">
                  Beschrijving
                  <input
                    type="text"
                    value={rbacRoleForm.description}
                    onChange={(event) =>
                      setRbacRoleForm((prev) => ({
                        ...prev,
                        description: event.target.value,
                      }))
                    }
                    className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm"
                    placeholder="Korte beschrijving"
                  />
                </label>
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs text-slate-600">Permissies</p>
                    <span className="text-xs text-slate-500">
                      {rbacRoleForm.permissionKeys.length} geselecteerd
                    </span>
                  </div>
                  <input
                    type="search"
                    value={rbacPermissionSearch}
                    onChange={(event) => setRbacPermissionSearch(event.target.value)}
                    className="w-full rounded border border-slate-300 px-3 py-2 text-xs"
                    placeholder="Zoek op naam of sleutel, bijvoorbeeld assets.view"
                    aria-label="Permissies zoeken"
                  />
                  <div className="max-h-[32rem] overflow-y-auto rounded border border-slate-200 p-2">
                    {rbacPermissionGroups.length === 0 ? (
                      <p className="text-xs text-slate-500">Geen permissies beschikbaar.</p>
                    ) : filteredRbacPermissionGroups.length === 0 ? (
                      <p className="text-xs text-slate-500">Geen permissies gevonden.</p>
                    ) : (
                      <div className="space-y-2">
                        {filteredRbacPermissionGroups.map((group) => {
                          const selectedCount = group.permissions.filter((permission) =>
                            rbacRoleForm.permissionKeys.includes(permission.key)
                          ).length;
                          const allSelected = selectedCount === group.permissions.length;
                          const isExpanded = Boolean(rbacPermissionSearch.trim()) || Boolean(
                            rbacExpandedPermissionGroups[group.group]
                          );

                          return (
                            <div key={group.group} className="rounded border border-slate-200 bg-white">
                              <div className="flex items-center gap-2 p-2">
                                <button
                                  type="button"
                                  onClick={() => toggleRbacPermissionGroup(group.group)}
                                  className="min-w-0 flex-1 text-left text-xs font-semibold text-slate-800"
                                  aria-expanded={isExpanded}
                                >
                                  <span className="mr-1 text-slate-500">{isExpanded ? "▾" : "▸"}</span>
                                  {group.group}
                                  <span className="ml-2 font-normal text-slate-500">
                                    {selectedCount} van {group.permissions.length}
                                  </span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setRbacPermissionGroupSelection(group.permissions, !allSelected)}
                                  className="rounded border border-slate-300 px-2 py-1 text-[11px] text-slate-700 hover:bg-slate-50"
                                >
                                  {allSelected ? "Wis" : "Alles"}
                                </button>
                              </div>
                              {isExpanded ? (
                                <div className="space-y-1 border-t border-slate-100 px-3 py-2">
                              {group.permissions.map((permission) => {
                                const key = String(permission?.key || "");
                                const checked = rbacRoleForm.permissionKeys.includes(key);
                                return (
                                  <label
                                    key={key}
                                    className="flex items-center gap-2 text-xs text-slate-700"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={(event) =>
                                        toggleRbacRolePermission(key, event.target.checked)
                                      }
                                    />
                                    <span>
                                      {permission.label || key}
                                      <span className="ml-1 text-slate-400">{key}</span>
                                    </span>
                                  </label>
                                );
                              })}
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={saveRbacRole}
                    disabled={savingKey === "rbac.role.save"}
                    className="rounded-lg bg-blue-600 px-3 py-2 text-xs text-white hover:bg-blue-700 disabled:opacity-60"
                  >
                    {savingKey === "rbac.role.save"
                      ? "Opslaan..."
                      : rbacEditingRoleId
                        ? "Rol opslaan"
                        : "Rol aanmaken"}
                  </button>
                  {rbacEditingRoleId && (
                    <button
                      type="button"
                      onClick={resetRbacRoleForm}
                      className="rounded-lg border border-slate-300 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50"
                    >
                      Bewerken annuleren
                    </button>
                  )}
                </div>
              </div>

              <div className="rounded-lg border p-4 space-y-2">
                <h4 className="text-sm font-semibold text-slate-800">Rollen</h4>
                <div className="max-h-72 overflow-y-auto">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="border-b text-left text-slate-600">
                        <th className="py-2 pr-2">Naam</th>
                        <th className="py-2 pr-2">Type</th>
                        <th className="py-2 pr-2">Permissies</th>
                        <th className="py-2 text-right">Acties</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rbacConfig.roles.map((role) => {
                        const roleId = String(role?._id || "");
                        const isSystem = Boolean(role?.system);
                        return (
                          <tr key={roleId} className="border-b align-top">
                            <td className="py-2 pr-2">
                              <div className="font-medium text-slate-800">
                                {role.name || "-"}
                              </div>
                              <div className="text-slate-500">{role.description || "-"}</div>
                            </td>
                            <td className="py-2 pr-2">
                              {isSystem ? (
                                <span className="rounded bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700">
                                  System
                                </span>
                              ) : (
                                <span className="rounded bg-blue-50 px-2 py-0.5 text-[11px] text-blue-700">
                                  Tenant
                                </span>
                              )}
                            </td>
                            <td className="py-2 pr-2 text-slate-600">
                              {Array.isArray(role.permissions) ? role.permissions.length : 0}
                            </td>
                            <td className="py-2 text-right">
                              <div className="flex justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => startRbacRoleEdit(role)}
                                  disabled={isSystem}
                                  className="rounded border border-slate-300 px-2 py-1 text-[11px] text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                                >
                                  Bewerk
                                </button>
                                <button
                                  type="button"
                                  onClick={() => deleteRbacRole(role)}
                                  disabled={
                                    isSystem ||
                                    savingKey === `rbac.role.delete.${roleId}`
                                  }
                                  className="rounded border border-red-300 px-2 py-1 text-[11px] text-red-700 hover:bg-red-50 disabled:opacity-50"
                                >
                                  Verwijder
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="rounded-lg border p-4 space-y-2">
              <h4 className="text-sm font-semibold text-slate-800">Gebruikerrollen</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="border-b text-left text-slate-600">
                      <th className="py-2 pr-3">Gebruiker</th>
                      <th className="py-2 pr-3">Rollen</th>
                      <th className="py-2 text-right">Actie</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rbacConfig.users.map((rbacUser) => {
                      const userId = String(rbacUser?._id || "");
                      const selectedRoleIds = Array.isArray(rbacUserRoleDrafts[userId])
                        ? rbacUserRoleDrafts[userId]
                        : [];
                      return (
                        <tr key={userId} className="border-b align-top">
                          <td className="py-2 pr-3">
                            <div className="font-medium text-slate-800">
                              {rbacUser.naam || "-"}
                            </div>
                            <div className="text-slate-500">{rbacUser.email || "-"}</div>
                          </td>
                          <td className="py-2 pr-3">
                            <div className="flex flex-wrap gap-2">
                              {rbacConfig.roles.map((role) => {
                                const roleId = String(role?._id || "");
                                const checked = selectedRoleIds.includes(roleId);
                                return (
                                  <label
                                    key={`${userId}-${roleId}`}
                                    className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={(event) =>
                                        toggleRbacUserRoleDraft(
                                          userId,
                                          roleId,
                                          event.target.checked
                                        )
                                      }
                                    />
                                    <span className="text-[11px]">
                                      {role.name || roleId}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                          </td>
                          <td className="py-2 text-right">
                            <button
                              type="button"
                              onClick={() => saveRbacUserRoles(userId)}
                              disabled={savingKey === `rbac.userroles.save.${userId}`}
                              className="rounded bg-slate-900 px-2.5 py-1.5 text-[11px] text-white hover:bg-slate-800 disabled:opacity-60"
                            >
                              {savingKey === `rbac.userroles.save.${userId}`
                                ? "Opslaan..."
                                : "Rollen opslaan"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <section className="bg-white rounded-xl shadow p-5 space-y-4">
            <h3 className="text-lg font-semibold">Rollenmatrix</h3>
            <p className="text-sm text-gray-600">
              Overzicht van welke rollen mogen kijken, uitvoeren of beheren binnen de tenant.
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-slate-600">
                    <th className="py-2 pr-3">Domein</th>
                    <th className="py-2 pr-3">Viewer</th>
                    <th className="py-2 pr-3">Monteur</th>
                    <th className="py-2 pr-3">Werkvoorbereider</th>
                    <th className="py-2 pr-3">Projectleider</th>
                    <th className="py-2 pr-3">Controller</th>
                    <th className="py-2 pr-3">TenantAdmin/Admin</th>
                  </tr>
                </thead>
                <tbody>
                  {ROLE_MATRIX_ROWS.map((row) => (
                    <tr key={row.domain} className="border-b align-top">
                      <td className="py-2 pr-3 font-medium text-slate-800">{row.domain}</td>
                      <td className="py-2 pr-3">{row.viewer}</td>
                      <td className="py-2 pr-3">{row.monteur}</td>
                      <td className="py-2 pr-3">{row.werkvoorbereider}</td>
                      <td className="py-2 pr-3">{row.projectleider}</td>
                      <td className="py-2 pr-3">{row.controller}</td>
                      <td className="py-2 pr-3">{row.tenantadmin}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="bg-white rounded-xl shadow p-5 space-y-4">
            <h3 className="text-lg font-semibold">Monteur menu-aanbod</h3>
            <p className="text-sm text-gray-600">
              Kies per categorie welke onderdelen zichtbaar zijn in het linker menu voor monteurs.
            </p>

            {isPlatformAdmin ? (
              <PolicyToggle
                label="Vrijgave voor tenant admin"
                description="Als dit uit staat, kan alleen Platform Admin monteur-menu wijzigen."
                checked={monteurMenuPolicy.editableByTenant}
                onChange={updateMonteurMenuPolicy}
                disabled={savingKey === "monteurMenuPolicy"}
              />
            ) : (
              <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                {monteurMenuPolicy.editableByTenant
                  ? "Deze tenant is vrijgegeven: je kunt monteur-menu wijzigen."
                  : "Deze tenant is nog niet vrijgegeven door Platform Admin. Je kunt het menu nu alleen bekijken."}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {MONTEUR_MENU_PRESETS.map((preset) => (
                <button
                  key={preset.key}
                  type="button"
                  onClick={() => applyMonteurMenuPreset(preset.key)}
                  disabled={savingKey === "monteurMenu" || !canEditMonteurMenu}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                  title={preset.description}
                >
                  {preset.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => {
                  setMonteurMenu(defaultMonteurMenu());
                  setSuccess('Alles teruggezet naar standaard. Klik nog op "Monteur menu opslaan".');
                  setError("");
                }}
                disabled={savingKey === "monteurMenu" || !canEditMonteurMenu}
                className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 hover:bg-amber-100 disabled:opacity-60"
              >
                Herstel alles
              </button>
            </div>

            <div className="space-y-3">
              {MONTEUR_MENU_SECTIONS.map((section) => {
                const categoryState = monteurMenu?.[section.key] || {};
                return (
                  <div key={section.key} className="rounded-xl border p-4 space-y-3">
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => resetMonteurMenuCategory(section.key)}
                        disabled={savingKey === "monteurMenu" || !canEditMonteurMenu}
                        className="rounded border border-slate-300 px-2.5 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                      >
                        Herstel categorie
                      </button>
                    </div>
                    <PolicyToggle
                      label={section.label}
                      description={section.description}
                      checked={categoryState.enabled !== false}
                      onChange={(value) => updateMonteurMenuCategory(section.key, value)}
                      disabled={savingKey === "monteurMenu" || !canEditMonteurMenu}
                    />

                    <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                      {section.items.map((item) => (
                        <PolicyToggle
                          key={item.key}
                          label={item.label}
                          description={item.description}
                          checked={categoryState?.items?.[item.key] !== false}
                          onChange={(value) =>
                            updateMonteurMenuItem(section.key, item.key, value)
                          }
                          disabled={
                            savingKey === "monteurMenu" ||
                            categoryState.enabled === false ||
                            !canEditMonteurMenu
                          }
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              onClick={saveMonteurMenu}
              disabled={savingKey === "monteurMenu" || !canEditMonteurMenu}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {savingKey === "monteurMenu" ? "Opslaan..." : "Monteur menu opslaan"}
            </button>
          </section>

          <section className="bg-white rounded-xl shadow p-5 space-y-4">
            <h3 className="text-lg font-semibold">Monteur actiebeleid</h3>
            <div className="grid gap-3 md:grid-cols-3">
              <PolicyToggle
                label="Monteur mag acties maken"
                description="Monteur kan eigen actie/reminder toevoegen op werkbon."
                checked={actionPolicy.allowMonteurCreate}
                disabled={savingKey === "allowMonteurCreate"}
                onChange={(value) => updateActionPolicy("allowMonteurCreate", value)}
              />
              <PolicyToggle
                label="Monteur mag eigen acties bewerken"
                description="Monteur kan alleen eigen acties aanpassen."
                checked={actionPolicy.allowMonteurEditOwn}
                disabled={savingKey === "allowMonteurEditOwn"}
                onChange={(value) => updateActionPolicy("allowMonteurEditOwn", value)}
              />
              <PolicyToggle
                label="Monteur mag eigen acties sluiten"
                description="Monteur kan alleen eigen acties sluiten."
                checked={actionPolicy.allowMonteurCloseOwn}
                disabled={savingKey === "allowMonteurCloseOwn"}
                onChange={(value) => updateActionPolicy("allowMonteurCloseOwn", value)}
              />
            </div>
          </section>

          <section className="bg-white rounded-xl shadow p-5 space-y-4">
            <h3 className="text-lg font-semibold">Inkoopbeleid</h3>
            <p className="text-sm text-gray-600">
              Stel in vanaf welk bedrag goedkeuring verplicht is en wat monteurs maximaal mogen bestellen.
            </p>

            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={Boolean(procurementPolicy.allowUnlinkedOrders)}
                onChange={(e) =>
                  setProcurementPolicy((prev) => ({
                    ...prev,
                    allowUnlinkedOrders: e.target.checked,
                  }))
                }
              />
              Aanmaken zonder project/werkbon toestaan
            </label>

            <div className="grid gap-3 md:grid-cols-3">
              <NumberField
                label="Goedkeuring vanaf (€)"
                value={procurementPolicy.requireApprovalAbove}
                onChange={(value) =>
                  setProcurementPolicy((prev) => ({
                    ...prev,
                    requireApprovalAbove: Number(value || 0),
                  }))
                }
              />
              <NumberField
                label="Monteur limiet (€)"
                value={procurementPolicy.technicianOrderLimit}
                onChange={(value) =>
                  setProcurementPolicy((prev) => ({
                    ...prev,
                    technicianOrderLimit: Number(value || 0),
                  }))
                }
              />
              <TextField
                label="Goedkeurrollen (komma gescheiden)"
                value={procurementPolicy.approvalRolesText}
                onChange={(value) =>
                  setProcurementPolicy((prev) => ({
                    ...prev,
                    approvalRolesText: value,
                  }))
                }
              />
            </div>

            <button
              type="button"
              onClick={saveProcurementPolicy}
              disabled={savingKey === "procurementPolicy"}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {savingKey === "procurementPolicy" ? "Opslaan..." : "Inkoopbeleid opslaan"}
            </button>
          </section>
          <section className="bg-white rounded-xl shadow p-5 space-y-4">
            <h3 className="text-lg font-semibold">Standaard leveranciersbibliotheek</h3>
            <p className="text-sm text-gray-600">
              Rollout-toggle voor template-import vanuit de platformbibliotheek (2BA referentie).
            </p>
            <PolicyToggle
              label="SupplierTemplate bibliotheek actief"
              description="Als dit uit staat, zijn supplier-template endpoints geblokkeerd voor deze tenant."
              checked={supplierTemplateLibraryEnabled}
              disabled={savingKey === "supplierTemplateLibrary.enabled"}
              onChange={toggleSupplierTemplateLibrary}
            />
          </section>

          <section className="bg-white rounded-xl shadow p-5 space-y-4">
            <h3 className="text-lg font-semibold">ZZP-modus</h3>
            <p className="text-sm text-gray-600">
              Flexibele werkbonflow voor kleine teams: monteur mag werkbonnen op klantniveau aanmaken zonder verplicht project.
            </p>
            <PolicyToggle
              label="ZZP-modus actief"
              description="Bij openstaande betaling blijven werkbon-routes schrijfbaar en projectkeuze voor monteur wordt optioneel."
              checked={zzpModeEnabled}
              disabled={savingKey === "zzpMode.enabled"}
              onChange={toggleZzpMode}
            />
          </section>

          {supplierTemplateLibraryEnabled ? (
            <SupplierTemplateImportPanel />
          ) : (
            <section className="bg-white rounded-xl shadow p-5">
              <p className="text-sm text-slate-600">
                SupplierTemplate import is uitgeschakeld. Zet eerst de module aan om templates te zoeken en te importeren.
              </p>
            </section>
          )}
        </div>
      )}

      {!loading && activeTab === "scopes" && (
        <section className="bg-white rounded-xl shadow p-5 space-y-4">
          <div>
            <h3 className="text-lg font-semibold">Scopebeheer</h3>
            <p className="text-sm text-gray-600">
              Definieer regio's, domeinen of klantscopes voor inzet en planning.
            </p>
          </div>

          <div className="grid gap-3 md:grid-cols-5">
            <SelectField
              label="Type"
              value={scopeDraft.type}
              options={[
                { value: "region", label: "Regio" },
                { value: "domain", label: "Domein" },
                { value: "account", label: "Klant/account" },
                { value: "team", label: "Team" },
                { value: "custom", label: "Custom" },
              ]}
              onChange={(value) => setScopeDraft((prev) => ({ ...prev, type: value }))}
            />
            <TextField
              label="Naam"
              value={scopeDraft.name}
              onChange={(value) => setScopeDraft((prev) => ({ ...prev, name: value }))}
            />
            <TextField
              label="Code"
              value={scopeDraft.code}
              onChange={(value) => setScopeDraft((prev) => ({ ...prev, code: value }))}
            />
            <PolicyToggle
              label="Actief"
              description="Nieuwe inzet en planning toegestaan."
              checked={scopeDraft.active}
              onChange={(value) => setScopeDraft((prev) => ({ ...prev, active: value }))}
              disabled={false}
            />
            <div className="flex items-end">
              <button
                type="button"
                onClick={createScope}
                disabled={savingKey === "scope.create"}
                className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white hover:bg-slate-800 disabled:opacity-60"
              >
                {savingKey === "scope.create" ? "Opslaan..." : "Scope aanmaken"}
              </button>
            </div>
          </div>

          {scopes.length === 0 ? (
            <p className="text-sm text-gray-500">Nog geen scopes ingericht.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-slate-600">
                    <th className="py-2 pr-3">Type</th>
                    <th className="py-2 pr-3">Naam</th>
                    <th className="py-2 pr-3">Code</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 text-right">Acties</th>
                  </tr>
                </thead>
                <tbody>
                  {scopes.map((scope) => (
                    <tr key={scope._id} className="border-b">
                      <td className="py-2 pr-3">{String(scope.type || "-")}</td>
                      <td className="py-2 pr-3">{String(scope.name || "-")}</td>
                      <td className="py-2 pr-3">{String(scope.code || "-")}</td>
                      <td className="py-2 pr-3">
                        {scope.active !== false ? (
                          <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700">Actief</span>
                        ) : (
                          <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">Inactief</span>
                        )}
                      </td>
                      <td className="py-2 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => toggleScopeActive(scope)}
                            disabled={savingKey === `scope.toggle.${scope._id}`}
                            className="rounded border border-slate-300 px-2.5 py-1 text-xs hover:bg-slate-50 disabled:opacity-60"
                          >
                            {scope.active !== false ? "Pauzeren" : "Activeren"}
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteScope(scope)}
                            disabled={savingKey === `scope.delete.${scope._id}`}
                            className="rounded border border-rose-200 px-2.5 py-1 text-xs text-rose-700 hover:bg-rose-50 disabled:opacity-60"
                          >
                            Verwijderen
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {!loading && activeTab === "toegangstest" && (
        <section className="bg-white rounded-xl shadow p-5 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="text-lg font-semibold">Automatische toegangstest</h3>
            <button
              onClick={loadAccessTestData}
              className="ml-auto rounded-lg border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
            >
              Testdata verversen
            </button>
          </div>

          {accessTestLoading ? (
            <p className="text-sm text-gray-500">Testdata laden...</p>
          ) : (
            <>
              <div className="flex flex-wrap items-end gap-3">
                <label className="block max-w-md flex-1">
                  <span className="block text-sm text-gray-600 mb-1">Gebruiker</span>
                  <select
                    className="w-full border rounded p-2"
                    value={accessSelectedUserId}
                    onChange={(e) => setAccessSelectedUserId(e.target.value)}
                  >
                    <option value="">Kies gebruiker</option>
                    {accessUsers.map((item) => (
                      <option key={item._id} value={item._id}>
                        {item.naam || item.email || item._id}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  onClick={runAccessApiTest}
                  disabled={!accessSelectedUserId || accessApiLoading}
                  className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white disabled:opacity-60"
                >
                  {accessApiLoading ? "Test draait..." : "Run echte API test"}
                </button>
              </div>

              {selectedAccessUser && (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <InfoCard
                    title="Roltype"
                    rows={[
                      { label: "Gebruiker", value: selectedAccessUser.naam || selectedAccessUser.email || "-" },
                      { label: "Rollen", value: accessRoleNames.join(", ") || "-" },
                      { label: "Subcontractor scope", value: isSubcontractor ? "Ja" : "Nee" },
                    ]}
                  />
                  <InfoCard
                    title="Scope resultaat"
                    rows={[
                      { label: "Zichtbare projecten", value: String(visibleProjects.length) },
                      { label: "Zichtbare taken", value: String(visibleTasks.length) },
                      { label: "Zichtbare werkbonnen", value: String(visibleWerkbonnen.length) },
                    ]}
                  />
                  <InfoCard
                    title="Project-lidmaatschappen"
                    rows={[
                      { label: "Toegewezen projecten", value: String(memberProjectIds.length) },
                      { label: "Niet-toegewezen projecten", value: String(Math.max(accessProjects.length - memberProjectIds.length, 0)) },
                    ]}
                  />
                  <InfoCard
                    title="Waarschuwingen"
                    rows={[
                      {
                        label: "Mogelijke taak-lekkages",
                        value: isSubcontractor ? String(leakedTasks.length) : "n.v.t.",
                      },
                    ]}
                  />
                </div>
              )}

              {selectedAccessUser && isSubcontractor && leakedTasks.length > 0 && (
                <section className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <h4 className="text-sm font-semibold text-amber-900">Controlepunt</h4>
                  <p className="mt-1 text-sm text-amber-800">
                    Er bestaan {leakedTasks.length} taken buiten projectlidmaatschap. Deze horen
                    volgens scope niet zichtbaar te zijn voor deze gebruiker.
                  </p>
                </section>
              )}

              {accessApiResult && (
                <section className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <h4 className="text-sm font-semibold text-slate-900">Echte API testresultaten</h4>
                  <div className="mt-2 grid gap-2 md:grid-cols-2 text-sm text-slate-700">
                    <p>Projecten: {accessApiResult?.scope?.projectsCount ?? "-"}</p>
                    <p>Taken: {accessApiResult?.scope?.tasksCount ?? "-"}</p>
                    <p>Werkbonnen: {accessApiResult?.scope?.workordersCount ?? "-"}</p>
                    <p>Mogelijke taak-lekkages: {accessApiResult?.diagnostics?.potentialTaskLeakCount ?? "-"}</p>
                  </div>
                </section>
              )}
            </>
          )}
        </section>
      )}

      {!loading && activeTab === "planning" && (
        <div className="space-y-4">
          <section className="bg-white rounded-xl shadow p-5 space-y-4">
            <div>
              <h3 className="text-lg font-semibold">Werkbontypes, workflows en rolweergave</h3>
              <p className="text-sm text-gray-600 mt-1">
                Stuur per werkbontype wat monteurs, projectleiders, tenant admins en klantportaal
                mogen zien, invullen of verplicht moeten afronden.
              </p>
              <p className="mt-1 text-xs text-slate-500">
                Actieve types ({activeWorkorderTypeCount}) zijn selecteerbaar bij het aanmaken van een werkbon.
              </p>
            </div>

            <div className="space-y-3">
              {workorderTypes.map((item, index) => (
                <div
                  key={`${item.key || "workorder-type"}-${index}`}
                  className="space-y-3 rounded-xl border p-4"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
                      Herkomst: {item.origin === "system" ? "Systeem" : "Tenant"}
                    </span>
                    {item.systemKey ? (
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
                        System key: {item.systemKey}
                      </span>
                    ) : null}
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs ${
                        item.requireHours === false
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-amber-100 text-amber-800"
                      }`}
                    >
                      Urenplicht: {item.requireHours === false ? "uit" : "aan"}
                    </span>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    <TextField
                      label="Naam"
                      value={item.name}
                      onChange={(value) => updateWorkorderType(index, "name", value)}
                    />
                    <TextField
                      label="Key"
                      value={item.key}
                      onChange={(value) => updateWorkorderType(index, "key", value)}
                    />
                    <TextField
                      label="Beschrijving"
                      value={item.description}
                      onChange={(value) => updateWorkorderType(index, "description", value)}
                    />
                    <TextField
                      label="Doel van dit type"
                      value={item.purpose}
                      onChange={(value) => updateWorkorderType(index, "purpose", value)}
                    />
                    <NumberField
                      label="Sorteervolgorde"
                      value={item.sortOrder}
                      onChange={(value) => updateWorkorderType(index, "sortOrder", value)}
                    />
                    <SelectField
                      label="Standaard uurtype"
                      value={item.defaultHourTypeId || ""}
                      options={[
                        { value: "", label: "Geen standaard" },
                        ...hourTypes.map((hourType) => ({
                          value: String(hourType._id),
                          label: hourType.name,
                        })),
                      ]}
                      onChange={(value) => updateWorkorderType(index, "defaultHourTypeId", value)}
                    />
                  </div>

                  <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                    <label className="flex items-center gap-2 rounded border px-3 py-2 text-sm">
                      <input
                        type="checkbox"
                        checked={item.active !== false}
                        onChange={(e) => updateWorkorderType(index, "active", e.target.checked)}
                      />
                      Actief (zichtbaar bij aanmaken)
                    </label>
                    <label className="flex items-center gap-2 rounded border px-3 py-2 text-sm">
                      <input
                        type="checkbox"
                        checked={Boolean(item.signatureRequired)}
                        onChange={(e) =>
                          setWorkorderTypes((prev) =>
                            prev.map((row, rowIndex) =>
                              rowIndex === index
                                ? {
                                    ...row,
                                    signatureRequired: e.target.checked,
                                    uiPolicy: normalizeWorkorderUiPolicy(row?.uiPolicy, {
                                      workorderKey:
                                        row?.key || row?.systemKey || row?.name || `type_${index + 1}`,
                                      signatureRequired: e.target.checked,
                                    }),
                                  }
                                : row
                            )
                          )
                        }
                      />
                      Handtekening verplicht
                    </label>
                    <label className="flex items-center gap-2 rounded border px-3 py-2 text-sm">
                      <input
                        type="checkbox"
                        checked={item.generateWorkReport !== false}
                        onChange={(e) =>
                          updateWorkorderType(index, "generateWorkReport", e.target.checked)
                        }
                      />
                      Werkrapport genereren
                    </label>
                    <label className="flex items-center gap-2 rounded border px-3 py-2 text-sm">
                      <input
                        type="checkbox"
                        checked={Boolean(item.moneybirdSync)}
                        onChange={(e) =>
                          updateWorkorderType(index, "moneybirdSync", e.target.checked)
                        }
                      />
                      Moneybird sync
                    </label>
                    <label className="flex items-center gap-2 rounded border px-3 py-2 text-sm">
                      <input
                        type="checkbox"
                        checked={Boolean(item.publishToClientHub)}
                        onChange={(e) =>
                          updateWorkorderType(index, "publishToClientHub", e.target.checked)
                        }
                      />
                      Publiceren in Klant Hub
                    </label>
                    <label className="flex items-center gap-2 rounded border px-3 py-2 text-sm">
                      <input
                        type="checkbox"
                        checked={item.allowNacalculatie !== false}
                        onChange={(e) =>
                          updateWorkorderType(index, "allowNacalculatie", e.target.checked)
                        }
                      />
                      Nacalculatie
                    </label>
                    <label className="flex items-center gap-2 rounded border px-3 py-2 text-sm">
                      <input
                        type="checkbox"
                        checked={item.requireHours !== false}
                        onChange={(e) =>
                          setWorkorderTypes((prev) =>
                            prev.map((row, rowIndex) =>
                              rowIndex === index
                                ? {
                                    ...row,
                                    requireHours: e.target.checked,
                                    workflow: {
                                      ...(row?.workflow && typeof row.workflow === "object"
                                        ? row.workflow
                                        : {}),
                                      requireHours: e.target.checked,
                                    },
                                  }
                                : row
                            )
                          )
                        }
                      />
                      Uren verplicht bij afronden
                    </label>
                  </div>

                  <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                    {(() => {
                      const presetKey = inferWorkorderUiPresetKey(item?.uiPolicy, {
                        workorderKey: item?.key || item?.systemKey || item?.name || `type_${index + 1}`,
                        signatureRequired: Boolean(item?.signatureRequired),
                      });
                      const selectedPreset =
                        WORKORDER_UI_PRESET_OPTIONS.find((option) => option.value === presetKey) ||
                        WORKORDER_UI_PRESET_OPTIONS[WORKORDER_UI_PRESET_OPTIONS.length - 1];

                      return (
                        <div className="grid gap-3 rounded-xl border border-blue-200 bg-blue-50/70 p-4 lg:grid-cols-[minmax(0,1fr)_auto]">
                          <div className="space-y-2">
                            <div>
                              <h4 className="text-sm font-semibold text-slate-900">
                                Preset rolweergave
                              </h4>
                              <p className="mt-1 text-xs text-slate-600">
                                Kies eerst een herkenbaar profiel per werkbontype. Daarna kun je
                                per rol verfijnen.
                              </p>
                            </div>
                            <div className="grid gap-3 md:grid-cols-2">
                              <SelectField
                                label="Preset"
                                value={presetKey}
                                onChange={(value) => applyWorkorderTypeUiPreset(index, value)}
                                options={WORKORDER_UI_PRESET_OPTIONS}
                              />
                              <div className="rounded-lg border border-white/70 bg-white/80 px-3 py-2 text-sm text-slate-700">
                                <p className="font-medium text-slate-900">{selectedPreset.label}</p>
                                <p className="mt-1 text-xs text-slate-500">
                                  {selectedPreset.description}
                                </p>
                              </div>
                            </div>
                            <p className="text-xs text-slate-500">
                              Live afgedwongen in monteur-app: layout, uren, materiaal, kosten, PDF,
                              handtekening en foto&apos;s. Checklist-instellingen zijn voorbereid voor
                              een volgende stap.
                            </p>
                          </div>
                          <div className="flex items-start">
                            <button
                              type="button"
                              onClick={() => applyWorkorderTypeUiPreset(index, "auto_default")}
                              className="rounded-lg border border-blue-300 bg-white px-4 py-2 text-sm text-blue-700 hover:bg-blue-50"
                            >
                              Herstel type-default
                            </button>
                          </div>
                        </div>
                      );
                    })()}

                    <div>
                      <h4 className="text-sm font-semibold text-slate-900">Rolweergave op werkbon</h4>
                      <p className="mt-1 text-xs text-slate-500">
                        Per rol bepaal je layout, zichtbaarheid, invoer en verplichtingen.
                      </p>
                    </div>

                    <details className="rounded-xl border border-slate-200 bg-white p-4">
                      <summary className="cursor-pointer text-sm font-semibold text-slate-900">
                        Geavanceerde rolrechten
                      </summary>
                      <p className="mt-2 text-xs text-slate-500">
                        Gebruik dit alleen voor uitzonderingen op het gekozen preset.
                      </p>

                    <div className="mt-4 grid gap-3 xl:grid-cols-2">
                      {WORKORDER_UI_ROLE_OPTIONS.map((role) => {
                        const rolePolicy = normalizeWorkorderUiPolicy(item?.uiPolicy, {
                          workorderKey: item?.key || item?.systemKey || item?.name || `type_${index + 1}`,
                          signatureRequired: Boolean(item?.signatureRequired),
                        }).roles[role.key];

                        return (
                          <div
                            key={`${item.key || "workorder"}-${role.key}`}
                            className="rounded-xl border bg-white p-4 space-y-3"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <h5 className="text-sm font-semibold text-slate-900">{role.label}</h5>
                              <div className="min-w-[180px]">
                                <SelectField
                                  label="Layout"
                                  value={rolePolicy.layout}
                                  onChange={(value) =>
                                    updateWorkorderTypeUiRole(index, role.key, "layout", value)
                                  }
                                  options={WORKORDER_UI_LAYOUT_OPTIONS}
                                />
                              </div>
                            </div>

                            <div className="grid gap-2 md:grid-cols-2">
                              <PolicyToggle
                                label="Uren tonen"
                                description="Toon urenblok op werkbon."
                                checked={rolePolicy.showHours}
                                onChange={(value) =>
                                  updateWorkorderTypeUiRole(index, role.key, "showHours", value)
                                }
                              />
                              <PolicyToggle
                                label="Uren invullen"
                                description="Sta urenregistratie toe."
                                checked={rolePolicy.editHours}
                                onChange={(value) =>
                                  updateWorkorderTypeUiRole(index, role.key, "editHours", value)
                                }
                              />
                              <PolicyToggle
                                label="Materiaal tonen"
                                description="Toon materiaalblok."
                                checked={rolePolicy.showMaterials}
                                onChange={(value) =>
                                  updateWorkorderTypeUiRole(index, role.key, "showMaterials", value)
                                }
                              />
                              <PolicyToggle
                                label="Materiaal invullen"
                                description="Sta materiaalregistratie toe."
                                checked={rolePolicy.editMaterials}
                                onChange={(value) =>
                                  updateWorkorderTypeUiRole(index, role.key, "editMaterials", value)
                                }
                              />
                              <PolicyToggle
                                label="Kosten tonen"
                                description="Toon bedragen en totalen."
                                checked={rolePolicy.showCosts}
                                onChange={(value) =>
                                  updateWorkorderTypeUiRole(index, role.key, "showCosts", value)
                                }
                              />
                              <PolicyToggle
                                label="PDF tonen"
                                description="Sta PDF/werkbon-download toe."
                                checked={rolePolicy.showPdf}
                                onChange={(value) =>
                                  updateWorkorderTypeUiRole(index, role.key, "showPdf", value)
                                }
                              />
                              <PolicyToggle
                                label="Handtekening tonen"
                                description="Toon klant-handtekening blok."
                                checked={rolePolicy.showSignature}
                                onChange={(value) =>
                                  updateWorkorderTypeUiRole(index, role.key, "showSignature", value)
                                }
                              />
                              <PolicyToggle
                                label="Handtekening verplicht"
                                description="Maak ondertekenen verplicht."
                                checked={rolePolicy.requireSignature}
                                onChange={(value) =>
                                  updateWorkorderTypeUiRole(index, role.key, "requireSignature", value)
                                }
                              />
                              <PolicyToggle
                                label="Handtekening invullen"
                                description="Sta invoeren of wissen van klant-akkoord toe."
                                checked={rolePolicy.editSignature}
                                onChange={(value) =>
                                  updateWorkorderTypeUiRole(index, role.key, "editSignature", value)
                                }
                              />
                              <PolicyToggle
                                label="Foto's tonen"
                                description="Toon foto- en bewijsblok."
                                checked={rolePolicy.showPhotos}
                                onChange={(value) =>
                                  updateWorkorderTypeUiRole(index, role.key, "showPhotos", value)
                                }
                              />
                              <PolicyToggle
                                label="Foto's beheren"
                                description="Sta uploaden en verwijderen van foto's toe."
                                checked={rolePolicy.editPhotos}
                                onChange={(value) =>
                                  updateWorkorderTypeUiRole(index, role.key, "editPhotos", value)
                                }
                              />
                              <PolicyToggle
                                label="Foto verplicht"
                                description="Eis bewijsfoto bij afronden."
                                checked={rolePolicy.requirePhotos}
                                onChange={(value) =>
                                  updateWorkorderTypeUiRole(index, role.key, "requirePhotos", value)
                                }
                              />
                              <PolicyToggle
                                label="Checklist tonen"
                                description="Voorbereid: checklistblok voor volgende stap."
                                checked={rolePolicy.showChecklist}
                                onChange={(value) =>
                                  updateWorkorderTypeUiRole(index, role.key, "showChecklist", value)
                                }
                              />
                              <PolicyToggle
                                label="Checklist verplicht"
                                description="Voorbereid: checklist moet compleet zijn."
                                checked={rolePolicy.requireChecklist}
                                onChange={(value) =>
                                  updateWorkorderTypeUiRole(index, role.key, "requireChecklist", value)
                                }
                              />
                              <PolicyToggle
                                label="Checklist invullen"
                                description="Voorbereid: sta checklist-mutaties toe."
                                checked={rolePolicy.editChecklist}
                                onChange={(value) =>
                                  updateWorkorderTypeUiRole(index, role.key, "editChecklist", value)
                                }
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    </details>
                  </div>

                  <div className="flex items-end gap-2">
                    <button
                      type="button"
                      onClick={() => removeWorkorderType(index)}
                      className="rounded border border-red-200 px-3 py-2 text-sm text-red-700 hover:bg-red-50"
                      disabled={workorderTypes.length <= 1}
                    >
                      Verwijder
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={addWorkorderType}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50"
              >
                Werkbontype toevoegen
              </button>
              <button
                type="button"
                onClick={saveWorkorderTypes}
                disabled={savingKey === "workorderTypes"}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {savingKey === "workorderTypes" ? "Opslaan..." : "Werkbontypes opslaan"}
              </button>
            </div>
            </section>
          )}

          <section className="bg-white rounded-xl shadow p-5 space-y-4">
            <div>
              <h3 className="text-lg font-semibold">Afrondvereisten werkbon</h3>
              <p className="text-sm text-gray-600 mt-1">
                Bepaal per tenant wat verplicht is voordat een werkbon afgerond mag worden.
              </p>
            </div>

            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <PolicyToggle
                label="Daglog verplicht"
                description="Afronden vereist uitvoeringstekst of daglog."
                checked={completionRequirements.requireExecutionLog}
                disabled={savingKey === "requireExecutionLog"}
                onChange={(value) => updateCompletionRequirement("requireExecutionLog", value)}
              />
              <PolicyToggle
                label="Uren verplicht"
                description="Afronden vereist uren of expliciete 0-uren bevestiging."
                checked={completionRequirements.requireHours}
                disabled={savingKey === "requireHours"}
                onChange={(value) => updateCompletionRequirement("requireHours", value)}
              />
              <PolicyToggle
                label="Materiaal verplicht"
                description="Zet uit als materiaal op dit tenanttype meestal niet geregistreerd wordt."
                checked={completionRequirements.requireMaterials}
                disabled={savingKey === "requireMaterials"}
                onChange={(value) => updateCompletionRequirement("requireMaterials", value)}
              />
              <PolicyToggle
                label="Bewijs verplicht"
                description="Afronden vereist foto of bijlage, tenzij expliciet bevestigd."
                checked={completionRequirements.requireEvidence}
                disabled={savingKey === "requireEvidence"}
                onChange={(value) => updateCompletionRequirement("requireEvidence", value)}
              />
            </div>
          </section>
          )}

          <section className="bg-white rounded-xl shadow p-5 space-y-4">
            <div>
              <h3 className="text-lg font-semibold">Diensttypes</h3>
              <p className="text-sm text-gray-600 mt-1">
                Stel per tenant standaarddiensten in. De planning gebruikt deze tijden als voorstel,
                maar planners mogen ze per werkbon altijd aanpassen.
              </p>
            </div>

            <div className="space-y-3">
              {serviceShifts.map((shift, index) => (
                <div
                  key={`${shift.key || "shift"}-${index}`}
                  className="grid gap-3 rounded-xl border p-4 md:grid-cols-[1.2fr_1fr_1fr_120px_110px_auto]"
                >
                  <TextField
                    label="Naam"
                    value={shift.label}
                    onChange={(value) => updateServiceShift(index, "label", value)}
                  />
                  <TextField
                    label="Key"
                    value={shift.key}
                    onChange={(value) => updateServiceShift(index, "key", value)}
                  />
                  <ColorField
                    label="Kleur"
                    value={shift.color}
                    onChange={(value) => updateServiceShift(index, "color", value)}
                  />
                  <TimeField
                    label="Start"
                    value={shift.startTime}
                    onChange={(value) => updateServiceShift(index, "startTime", value)}
                  />
                  <TimeField
                    label="Einde"
                    value={shift.endTime}
                    onChange={(value) => updateServiceShift(index, "endTime", value)}
                  />
                  <div className="flex items-end gap-2">
                    <label className="flex items-center gap-2 rounded border px-3 py-2 text-sm">
                      <input
                        type="checkbox"
                        checked={shift.active !== false}
                        onChange={(e) => updateServiceShift(index, "active", e.target.checked)}
                      />
                      Actief
                    </label>
                    <button
                      type="button"
                      onClick={() => removeServiceShift(index)}
                      className="rounded border border-red-200 px-3 py-2 text-sm text-red-700 hover:bg-red-50"
                      disabled={serviceShifts.length <= 1}
                    >
                      Verwijder
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={addServiceShift}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50"
              >
                Diensttype toevoegen
              </button>
              <button
                type="button"
                onClick={saveServiceShifts}
                disabled={savingKey === "serviceShifts"}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {savingKey === "serviceShifts" ? "Opslaan..." : "Diensttypes opslaan"}
              </button>
            </div>
          </section>
        </div>
      )}

      {!loading && activeTab === "skills" && (
        <div className="space-y-4">
          <section className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            <h3 className="text-sm font-semibold text-amber-900">Voorbereiding: Skill-based planning</h3>
            <p className="mt-1 text-sm text-amber-800">
              Dit is een visuele voorbereiding in Tenant Admin. Opslag/API-koppeling volgt in een volgende fase.
            </p>
          </section>

          <section className="bg-white rounded-xl shadow p-5 space-y-4">
            <div>
              <h3 className="text-lg font-semibold">Skill catalogus (preview)</h3>
              <p className="text-sm text-gray-600">
                Definieer tenant-specifieke skills met stabiele keys en level-schaal.
              </p>
            </div>

            <div className="grid gap-3 md:grid-cols-4">
              <TextField
                label="Skill key"
                value={skillCatalogDraft.key}
                onChange={(value) => setSkillCatalogDraft((prev) => ({ ...prev, key: value }))}
              />
              <TextField
                label="Label"
                value={skillCatalogDraft.label}
                onChange={(value) => setSkillCatalogDraft((prev) => ({ ...prev, label: value }))}
              />
              <SelectField
                label="Level schaal"
                value={skillCatalogDraft.levelScale}
                options={[
                  { value: "3", label: "1-3" },
                  { value: "5", label: "1-5" },
                ]}
                onChange={(value) => setSkillCatalogDraft((prev) => ({ ...prev, levelScale: value }))}
              />
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={addSkillPreview}
                  className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white hover:bg-slate-800"
                >
                  Skill toevoegen
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-slate-600">
                    <th className="py-2 pr-3">Key</th>
                    <th className="py-2 pr-3">Label</th>
                    <th className="py-2 pr-3">Schaal</th>
                    <th className="py-2 pr-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {skillCatalogPreview.map((skill) => (
                    <tr key={skill.key} className="border-b">
                      <td className="py-2 pr-3 font-mono text-xs">{skill.key}</td>
                      <td className="py-2 pr-3">{skill.label}</td>
                      <td className="py-2 pr-3">1-{skill.levelScale}</td>
                      <td className="py-2 pr-3">
                        {skill.active !== false ? (
                          <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700">Actief</span>
                        ) : (
                          <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">Inactief</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="bg-white rounded-xl shadow p-5 space-y-4">
            <div>
              <h3 className="text-lg font-semibold">Werkbontype skillregels (preview)</h3>
              <p className="text-sm text-gray-600">
                Per type stel je straks required skills en gate mode in: <code>advice</code>, <code>warn</code>,{" "}
                <code>enforce</code>.
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-slate-600">
                    <th className="py-2 pr-3">Werkbontype</th>
                    <th className="py-2 pr-3">Key</th>
                    <th className="py-2 pr-3">Gate mode</th>
                    <th className="py-2 pr-3">Required skills</th>
                  </tr>
                </thead>
                <tbody>
                  {skillWorkorderPreviewRows.map((row) => (
                    <tr key={row.key || row.name} className="border-b">
                      <td className="py-2 pr-3">{row.name}</td>
                      <td className="py-2 pr-3 font-mono text-xs">{row.key || "-"}</td>
                      <td className="py-2 pr-3">
                        <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-700">{row.mode}</span>
                      </td>
                      <td className="py-2 pr-3">{row.requiredSkills || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="bg-white rounded-xl shadow p-5 space-y-3">
            <h3 className="text-lg font-semibold">Gate gedrag (doelbeeld)</h3>
            <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
              <li><strong>Advice:</strong> alleen ranking en tips.</li>
              <li><strong>Warn:</strong> waarschuwing met bevestiging.</li>
              <li><strong>Enforce:</strong> blokkeren tenzij override-permission + reden.</li>
            </ul>
          </section>
        </div>
      )}

      {!loading && activeTab === "facturatie" && (
        <div className="space-y-4">
          {billingError && (
            <section className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-sm text-red-700">{billingError}</p>
            </section>
          )}

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <InfoCard
              title="Abonnement"
              rows={[
                { label: "Plan", value: String(billingData.planCode || "basis") },
                { label: "Afschrijfmoment", value: formatDebitWindowLabel(billingData.preferredDebitWindow) },
                { label: "Status", value: subscriptionStatusLabel },
                { label: "Betaalstatus", value: paymentStatusLabel },
              ]}
            />
            <InfoCard
              title="Incasso"
              rows={[
                { label: "Mislukte pogingen", value: String(billingData.failedAttempts || 0) },
                { label: "Max pogingen", value: String(billingData.maxAttempts || 2) },
                { label: "Interval (dagen)", value: String(billingData.retryIntervalDays || 7) },
              ]}
            />
            <InfoCard
              title="Bedragen"
              rows={[
                { label: "Bedrag", value: formatCurrencyFromCents(billingData.amountCents, billingData.currency) },
                { label: "Valuta", value: String(billingData.currency || "EUR") },
                { label: "Laatste betaald", value: formatDateTime(billingData.lastPaymentDate) },
              ]}
            />
            <InfoCard
              title="Toegang"
              rows={[
                { label: "Limited mode", value: billingData.limitedMode ? "Ja" : "Nee" },
                { label: "Grace tot", value: formatDateTime(billingData.graceUntil) },
                { label: "Volgende retry", value: formatDateTime(billingData.nextRetryAt) },
                { label: "Volgende afschrijving", value: formatDate(billingData.nextDebitAt) },
              ]}
            />
          </div>

          <section className="bg-white rounded-xl shadow p-5 space-y-4">
            <h3 className="text-lg font-semibold">Facturatie-instellingen</h3>
            <p className="text-sm text-gray-600">
              Security fix: facturatievelden worden expliciet opgeslagen via dedicated endpoint.
            </p>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              {isPlatformBillingAdmin && (
                <TextField
                  label="Plan code"
                  value={billingData.planCode}
                  onChange={(value) =>
                    setBillingData((prev) => ({
                      ...prev,
                      planCode: value,
                    }))
                  }
                />
              )}
              <SelectField
                label="Afschrijfmoment"
                value={billingData.preferredDebitWindow}
                options={DEBIT_WINDOW_OPTIONS}
                onChange={(value) =>
                  setBillingData((prev) => ({
                    ...prev,
                    preferredDebitWindow: value,
                  }))
                }
              />
              {isPlatformBillingAdmin && (
                <NumberField
                  label="Bedrag (centen)"
                  value={billingData.amountCents}
                  onChange={(value) =>
                    setBillingData((prev) => ({
                      ...prev,
                      amountCents: value,
                    }))
                  }
                />
              )}
              {isPlatformBillingAdmin && (
                <NumberField
                  label="Max incassopogingen"
                  value={billingData.maxAttempts}
                  onChange={(value) =>
                    setBillingData((prev) => ({
                      ...prev,
                      maxAttempts: value,
                    }))
                  }
                />
              )}
              {isPlatformBillingAdmin && (
                <NumberField
                  label="Retry interval (dagen)"
                  value={billingData.retryIntervalDays}
                  onChange={(value) =>
                    setBillingData((prev) => ({
                      ...prev,
                      retryIntervalDays: value,
                    }))
                  }
                />
              )}
            </div>
            <p className="text-sm text-slate-600">
              Volgende afschrijving verwacht op: {formatDate(billingData.nextDebitAt)}
            </p>
            <button
              type="button"
              onClick={saveBillingSettings}
              disabled={billingSaving}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {billingSaving ? "Opslaan..." : "Facturatie opslaan"}
            </button>
          </section>

          {isPlatformBillingAdmin && (
            <section className="bg-white rounded-xl shadow p-5 space-y-4">
              <h3 className="text-lg font-semibold">Acties</h3>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <NumberField
                label="Betaallink bedrag (centen)"
                value={payForm.amountCents}
                onChange={(value) =>
                  setPayForm((prev) => ({
                    ...prev,
                    amountCents: String(value || 0),
                  }))
                }
              />
              <TextField
                label="Omschrijving"
                value={payForm.description}
                onChange={(value) =>
                  setPayForm((prev) => ({
                    ...prev,
                    description: value,
                  }))
                }
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={createPayLink}
                disabled={billingSaving}
                className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white hover:bg-slate-800 disabled:opacity-60"
              >
                Betaallink maken
              </button>
              <button
                type="button"
                onClick={retryBillingNow}
                disabled={billingSaving}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50 disabled:opacity-60"
              >
                Herincasso nu
              </button>
              <button
                type="button"
                onClick={restoreBillingAccess}
                disabled={billingSaving}
                className="rounded-lg border border-emerald-300 px-4 py-2 text-sm text-emerald-800 hover:bg-emerald-50 disabled:opacity-60"
              >
                Toegang herstellen
              </button>
              <button
                type="button"
                onClick={() => {
                  loadBilling();
                  loadBillingInvoices();
                }}
                disabled={billingLoading}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50 disabled:opacity-60"
              >
                {billingLoading ? "Laden..." : "Facturatie verversen"}
              </button>
            </div>
          </section>
          )}

          <section className="bg-white rounded-xl shadow p-5 space-y-4">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-lg font-semibold">Seat maandfacturen</h3>
              <button
                type="button"
                onClick={generateSeatInvoice}
                disabled={generatingSeatInvoice}
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-60"
              >
                {generatingSeatInvoice ? "Genereren..." : "Vorige maand genereren"}
              </button>
            </div>

            {seatInvoices.length === 0 ? (
              <p className="text-sm text-gray-500">
                Nog geen seat maandfacturen gegenereerd.
              </p>
            ) : (
              <div className="space-y-3">
                {seatInvoices.map((invoice, index) => (
                  <details
                    key={String(invoice._id || `${invoice.periodYear}-${invoice.periodMonth}-${index}`)}
                    className="rounded-lg border border-slate-200 p-3"
                  >
                    <summary className="cursor-pointer text-sm font-medium text-slate-800">
                      {String(invoice.periodMonth || "").padStart(2, "0")}/{invoice.periodYear} ·{" "}
                      {formatCurrencyFromCents(invoice.totalCents, invoice.currency || billingData.currency)} ·{" "}
                      status: {invoice.status || "generated"}
                    </summary>
                    <div className="mt-3 space-y-2 text-sm">
                      <p className="text-slate-600">
                        Seats start/eind: {invoice.activeSeatsAtStart || 0} → {invoice.activeSeatsAtEnd || 0} · inbegrepen: {invoice.includedSeats || 0}
                      </p>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-xs">
                          <thead>
                            <tr className="border-b text-left text-slate-500">
                              <th className="py-1 pr-2">Regel</th>
                              <th className="py-1 pr-2">Aantal</th>
                              <th className="py-1 pr-2">Bedrag</th>
                              <th className="py-1 pr-2">Details</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(Array.isArray(invoice.lines) ? invoice.lines : []).map((line, lineIndex) => (
                              <tr key={`${line.key || "line"}-${lineIndex}`} className="border-b">
                                <td className="py-1 pr-2">{line.label || line.key || "-"}</td>
                                <td className="py-1 pr-2">{line.quantity || 0}</td>
                                <td className="py-1 pr-2">
                                  {formatCurrencyFromCents(line.totalAmountCents, invoice.currency || billingData.currency)}
                                </td>
                                <td className="py-1 pr-2 text-slate-500">{line.details || "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </details>
                ))}
              </div>
            )}
          </section>

          <section className="bg-white rounded-xl shadow p-5 space-y-4">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-lg font-semibold">Moneybird facturen</h3>
              <span
                className={billingInvoicesConnected
                  ? "rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700"
                  : "rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700"}
              >
                {billingInvoicesConnected ? "Verbonden" : "Niet verbonden"}
              </span>
            </div>

            {billingInvoices.length === 0 ? (
              <p className="text-sm text-gray-500">Geen facturen beschikbaar.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-gray-600">
                      <th className="py-2 pr-3">Factuur</th>
                      <th className="py-2 pr-3">Status</th>
                      <th className="py-2 pr-3">Openstaand</th>
                      <th className="py-2 pr-3">Betaald</th>
                      <th className="py-2 pr-3">Vervaldatum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billingInvoices.map((invoice, index) => (
                      <tr key={String(invoice.id || invoice.invoiceId || index)} className="border-b">
                        <td className="py-2 pr-3">{invoice.invoiceId || invoice.reference || "-"}</td>
                        <td className="py-2 pr-3">{String(invoice.state || "-")}</td>
                        <td className="py-2 pr-3">
                          {formatAmount(invoice.totalUnpaid, invoice.currency || billingData.currency)}
                        </td>
                        <td className="py-2 pr-3">
                          {formatAmount(invoice.totalPaid, invoice.currency || billingData.currency)}
                        </td>
                        <td className="py-2 pr-3">{formatDate(invoice.dueDate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}

      {!loading && activeTab === "integraties" && (
        <div className="space-y-4">
          <section className="bg-white rounded-xl shadow p-5">
            <h3 className="text-lg font-semibold mb-3">Installaties &amp; logboek</h3>
            <p className="text-sm text-gray-600">
              Tenantmodule voor installaties, logboekregels, Service-ID en Logboek Access.
            </p>
            <button
              type="button"
              onClick={() => toggleFireSafetyModule(!fireSafetyModuleEnabled)}
              disabled={savingKey === "fireSafetyLogbook.enabled"}
              className={`mt-4 rounded-lg px-4 py-2 text-sm font-medium transition ${
                fireSafetyModuleEnabled
                  ? "bg-emerald-600 text-white hover:bg-emerald-700"
                  : "bg-slate-200 text-slate-700 hover:bg-slate-300"
              }`}
            >
              {savingKey === "fireSafetyLogbook.enabled"
                ? "Opslaan..."
                : fireSafetyModuleEnabled
                  ? "Ingeschakeld"
                  : "Uitgeschakeld"}
            </button>
          </section>

          <section className="bg-white rounded-xl shadow p-5">
            <h3 className="text-lg font-semibold mb-3">Moneybird</h3>
            <div className="grid gap-3 md:grid-cols-3">
              <PolicyToggle
                label="Module actief"
                description="Toon Moneybird in uren/factuur flow."
                checked={moneybirdState.enabled}
                disabled={savingKey === "moneybird.enabled"}
                onChange={(value) =>
                  updateIntegrationSetting("moneybird", "enabled", value)
                }
              />
              <PolicyToggle
                label="Verbonden"
                description="Zet aan na OAuth of succesvolle connectietest."
                checked={moneybirdState.connected}
                disabled={savingKey === "moneybird.connected"}
                onChange={(value) =>
                  updateIntegrationSetting("moneybird", "connected", value)
                }
              />
              <PolicyToggle
                label="Auto-sync factuur"
                description="Sync factuur direct bij afronden factuurflow."
                checked={moneybirdState.autoSyncOnInvoice}
                disabled={savingKey === "moneybird.autoSyncOnInvoice"}
                onChange={(value) =>
                  updateIntegrationSetting("moneybird", "autoSyncOnInvoice", value)
                }
              />
            </div>
          </section>

          <section className="bg-white rounded-xl shadow p-5">
            <h3 className="text-lg font-semibold mb-3">OCI catalogus (Technische Unie)</h3>

            <div className="grid gap-3 md:grid-cols-2 mb-3">
              <PolicyToggle
                label="Module actief"
                description="Toon OCI catalogus-flow in materiaalstroom."
                checked={tuState.enabled}
                disabled={savingKey === "tu.enabled"}
                onChange={(value) => updateIntegrationSetting("tu", "enabled", value)}
              />
              <PolicyToggle
                label="Verbonden"
                description={
                  tuState.hasPassword
                    ? "Credentials aanwezig, markeer als connectietest geslaagd."
                    : "Vul eerst gebruikersgegevens en wachtwoord in."
                }
                checked={tuState.connected}
                disabled={savingKey === "tu.connected"}
                onChange={(value) => updateIntegrationSetting("tu", "connected", value)}
              />
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <TextField
                label="Basis URL"
                value={tuState.baseUrl}
                onChange={(value) =>
                  setIntegrationSettings((prev) => ({
                    ...prev,
                    tu: { ...prev.tu, baseUrl: value },
                  }))
                }
              />
              <TextField
                label="Hook URL"
                value={tuState.hookUrl}
                onChange={(value) =>
                  setIntegrationSettings((prev) => ({
                    ...prev,
                    tu: { ...prev.tu, hookUrl: value },
                  }))
                }
              />
              <TextField
                label="Gebruikersnaam"
                value={tuState.username}
                onChange={(value) =>
                  setIntegrationSettings((prev) => ({
                    ...prev,
                    tu: { ...prev.tu, username: value },
                  }))
                }
              />
              <TextField
                label="Klantnummer"
                value={tuState.customerNumber}
                onChange={(value) =>
                  setIntegrationSettings((prev) => ({
                    ...prev,
                    tu: { ...prev.tu, customerNumber: value },
                  }))
                }
              />
              <PasswordField
                label={tuState.hasPassword ? "Wachtwoord (laat leeg om te behouden)" : "Wachtwoord"}
                value={tuPasswordInput}
                onChange={setTuPasswordInput}
              />
              <TextField
                label="Vendor"
                value={tuState.vendor}
                onChange={(value) =>
                  setIntegrationSettings((prev) => ({
                    ...prev,
                    tu: { ...prev.tu, vendor: value },
                  }))
                }
              />
              <SelectField
                label="Unit type"
                value={tuState.unitType}
                options={[
                  { value: "EAN", label: "EAN" },
                  { value: "ISO", label: "ISO" },
                ]}
                onChange={(value) =>
                  setIntegrationSettings((prev) => ({
                    ...prev,
                    tu: { ...prev.tu, unitType: value },
                  }))
                }
              />
            </div>

            <div className="grid gap-3 md:grid-cols-3 mt-3">
              <PolicyToggle
                label="Brutoprijs"
                description="Gebruik CUSTFIELD5=VAR:grossPrice."
                checked={tuState.includeGrossPrice}
                onChange={(value) =>
                  setIntegrationSettings((prev) => ({
                    ...prev,
                    tu: { ...prev.tu, includeGrossPrice: value },
                  }))
                }
              />
              <PolicyToggle
                label="Verwijderingsbijdrage"
                description="Neem disposal contribution mee in prijs."
                checked={tuState.includeDisposalContribution}
                onChange={(value) =>
                  setIntegrationSettings((prev) => ({
                    ...prev,
                    tu: { ...prev.tu, includeDisposalContribution: value },
                  }))
                }
              />
              <PolicyToggle
                label="GTIN"
                description="Vraag GTIN mee als custom field."
                checked={tuState.includeGtin}
                onChange={(value) =>
                  setIntegrationSettings((prev) => ({
                    ...prev,
                    tu: { ...prev.tu, includeGtin: value },
                  }))
                }
              />
              <PolicyToggle
                label="BTW percentage"
                description="Vraag VAT percentage mee in custom field."
                checked={tuState.includeVatPct}
                onChange={(value) =>
                  setIntegrationSettings((prev) => ({
                    ...prev,
                    tu: { ...prev.tu, includeVatPct: value },
                  }))
                }
              />
              <PolicyToggle
                label="Levertijd"
                description="Vraag leadtime in dagen mee."
                checked={tuState.includeLeadTime}
                onChange={(value) =>
                  setIntegrationSettings((prev) => ({
                    ...prev,
                    tu: { ...prev.tu, includeLeadTime: value },
                  }))
                }
              />
            </div>

            <div className="mt-4">
              <button
                onClick={saveTuConfig}
                disabled={savingKey === "tu.config"}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded disabled:opacity-60"
              >
                {savingKey === "tu.config" ? "Opslaan..." : "TU configuratie opslaan"}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

function InfoCard({ title, rows }) {
  return (
    <section className="bg-white rounded-xl shadow p-5">
      <h3 className="text-lg font-semibold mb-3">{title}</h3>
      <div className="space-y-2">
        {rows.map((row) => (
          <div key={row.label} className="flex items-start justify-between gap-4">
            <span className="text-sm text-gray-500">{row.label}</span>
            <span className="text-sm text-right">{row.value}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function PolicyToggle({ label, description, checked, onChange, disabled = false }) {
  return (
    <label className="rounded border p-3 flex items-start gap-3 cursor-pointer">
      <input
        type="checkbox"
        className="mt-1"
        checked={Boolean(checked)}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
      <span>
        <span className="block text-sm font-medium">{label}</span>
        <span className="block text-xs text-gray-500">{description}</span>
      </span>
    </label>
  );
}

function TextField({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="block text-sm text-gray-600 mb-1">{label}</span>
      <input
        type="text"
        className="w-full border rounded p-2"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

function NumberField({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="block text-sm text-gray-600 mb-1">{label}</span>
      <input
        type="number"
        min="0"
        className="w-full border rounded p-2"
        value={value ?? 0}
        onChange={(e) => onChange(Number(e.target.value || 0))}
      />
    </label>
  );
}

function PasswordField({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="block text-sm text-gray-600 mb-1">{label}</span>
      <input
        type="password"
        autoComplete="new-password"
        className="w-full border rounded p-2"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="block text-sm text-gray-600 mb-1">{label}</span>
      <select
        className="w-full border rounded p-2"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function TimeField({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="block text-sm text-gray-600 mb-1">{label}</span>
      <input
        type="time"
        className="w-full border rounded p-2"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

function ColorField({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="block text-sm text-gray-600 mb-1">{label}</span>
      <div className="flex items-center gap-2">
        <input
          type="color"
          className="h-10 w-14 rounded border"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        <input
          type="text"
          className="w-full border rounded p-2"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </label>
  );
}

function defaultMonteurMenu() {
  return normalizeMonteurMenu({});
}

function normalizeMonteurMenu(value) {
  const source = value || {};
  return {
    werk: {
      enabled: source?.werk?.enabled !== false,
      items: {
        werkbonnen: source?.werk?.items?.werkbonnen !== false,
        opnames: source?.werk?.items?.opnames !== false,
        siteSurvey: source?.werk?.items?.siteSurvey !== false,
      },
    },
    info: {
      enabled: source?.info?.enabled !== false,
      items: {
        meldingen: source?.info?.items?.meldingen !== false,
        fotos: source?.info?.items?.fotos !== false,
        formulieren: source?.info?.items?.formulieren !== false,
        notities: source?.info?.items?.notities !== false,
        toolbox: source?.info?.items?.toolbox !== false,
        documenten: source?.info?.items?.documenten !== false,
      },
    },
    instellingen: {
      enabled: source?.instellingen?.enabled !== false,
      items: {
        verlof: source?.instellingen?.items?.verlof !== false,
        weergave: source?.instellingen?.items?.weergave !== false,
      },
    },
  };
}

function buildMonteurMenuPayload(value) {
  const normalized = normalizeMonteurMenu(value);
  return {
    werk: {
      enabled: Boolean(normalized.werk.enabled),
      items: {
        werkbonnen: Boolean(normalized.werk.items.werkbonnen),
        opnames: Boolean(normalized.werk.items.opnames),
        siteSurvey: Boolean(normalized.werk.items.siteSurvey),
      },
    },
    info: {
      enabled: Boolean(normalized.info.enabled),
      items: {
        meldingen: Boolean(normalized.info.items.meldingen),
        fotos: Boolean(normalized.info.items.fotos),
        formulieren: Boolean(normalized.info.items.formulieren),
        notities: Boolean(normalized.info.items.notities),
        toolbox: Boolean(normalized.info.items.toolbox),
        documenten: Boolean(normalized.info.items.documenten),
      },
    },
    instellingen: {
      enabled: Boolean(normalized.instellingen.enabled),
      items: {
        verlof: Boolean(normalized.instellingen.items.verlof),
        weergave: Boolean(normalized.instellingen.items.weergave),
      },
    },
  };
}

function defaultMonteurMenuPolicy() {
  return normalizeMonteurMenuPolicy({});
}

function normalizeMonteurMenuPolicy(value) {
  const source = value || {};
  return {
    editableByTenant: Boolean(source.editableByTenant),
  };
}

function defaultProcurementPolicy() {
  return normalizeProcurementPolicy({});
}

function normalizeProcurementPolicy(value) {
  const source = value || {};
  const roles = Array.isArray(source.approvalRoles)
    ? source.approvalRoles
        .map((item) => String(item || "").trim().toLowerCase())
        .filter(Boolean)
    : ["controller", "projectleider", "tenantadmin", "admin", "platformadmin"];

  const uniqueRoles = Array.from(new Set(roles));

  return {
    allowUnlinkedOrders:
      source.allowUnlinkedOrders === undefined ? true : Boolean(source.allowUnlinkedOrders),
    requireApprovalAbove: Number(source.requireApprovalAbove || 0),
    technicianOrderLimit: Number(source.technicianOrderLimit || 0),
    approvalRoles: uniqueRoles,
    approvalRolesText: uniqueRoles.join(", "),
  };
}

function defaultBillingState() {
  return normalizeBilling({});
}

function normalizeBilling(value) {
  const source = value || {};
  return {
    planCode: String(source.planCode || "basis"),
    amountCents: Number.parseInt(String(source.amountCents || 0), 10) || 0,
    currency: String(source.currency || "EUR"),
    preferredDebitWindow: String(source.preferredDebitWindow || "start_of_month"),
    billingCycleDay: Number.parseInt(String(source.billingCycleDay || 1), 10) || 1,
    nextDebitAt: source.nextDebitAt || null,
    subscriptionStatus: String(source.subscriptionStatus || "active"),
    paymentStatus: String(source.paymentStatus || "paid"),
    failedAttempts: Number.parseInt(String(source.failedAttempts || 0), 10) || 0,
    maxAttempts: Number.parseInt(String(source.maxAttempts || 2), 10) || 2,
    retryIntervalDays: Number.parseInt(String(source.retryIntervalDays || 7), 10) || 7,
    limitedMode: Boolean(source.limitedMode),
    graceUntil: source.graceUntil || null,
    nextRetryAt: source.nextRetryAt || null,
    lastPaymentDate: source.lastPaymentDate || null,
  };
}

function formatDebitWindowLabel(value) {
  const key = String(value || "").toLowerCase();
  if (key === "start_of_month") return "Begin van de maand";
  if (key === "mid_month") return "Midden van de maand";
  if (key === "end_of_month") return "Eind van de maand";
  return "Begin van de maand";
}

function formatSubscriptionStatus(value) {
  const status = String(value || "").toLowerCase();
  if (status === "active") return "Actief";
  if (status === "limited") return "Beperkt";
  if (status === "past_due_1") return "Betaalachterstand 1";
  if (status === "past_due_2") return "Betaalachterstand 2";
  return status || "-";
}

function formatPaymentStatus(value) {
  const status = String(value || "").toLowerCase();
  if (status === "paid") return "Betaald";
  if (status === "failed") return "Mislukt";
  if (status === "canceled") return "Geannuleerd";
  if (status === "expired") return "Verlopen";
  if (status === "open") return "Open";
  return status || "-";
}

function formatCurrencyFromCents(cents, currency = "EUR") {
  const amount = Number(cents || 0) / 100;
  try {
    return new Intl.NumberFormat("nl-NL", {
      style: "currency",
      currency: String(currency || "EUR").toUpperCase(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return amount.toFixed(2) + " " + String(currency || "EUR").toUpperCase();
  }
}

function formatAmount(value, currency = "EUR") {
  const amount = Number(value || 0);
  try {
    return new Intl.NumberFormat("nl-NL", {
      style: "currency",
      currency: String(currency || "EUR").toUpperCase(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return amount.toFixed(2) + " " + String(currency || "EUR").toUpperCase();
  }
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("nl-NL").format(date);
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("nl-NL", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function formatBackupRunStatus(value) {
  const key = String(value || "").trim().toLowerCase();
  if (key === "ok") return "OK";
  if (key === "warning") return "Waarschuwing";
  if (key === "failed") return "Mislukt";
  if (key === "skipped") return "Overgeslagen";
  return "Onbekend";
}

function defaultBackupStatus() {
  return {
    updatedAt: null,
    runStartedAt: null,
    runFinishedAt: null,
    runStatus: "unknown",
    verifyMode: "fast",
    foldersTotal: 0,
    foldersBackedUp: 0,
    verifyOk: 0,
    verifyFailed: 0,
    lastError: "",
  };
}

function normalizeIntegrationSettings(value) {
  const integrations = value || {};
  const moneybird = integrations.moneybird || {};
  const tu = integrations.tu || {};

  return {
    moneybird: {
      enabled: Boolean(moneybird.enabled),
      connected: Boolean(moneybird.connected),
      autoSyncOnInvoice:
        moneybird.autoSyncOnInvoice === undefined
          ? true
          : Boolean(moneybird.autoSyncOnInvoice),
    },
    tu: {
      enabled: Boolean(tu.enabled),
      connected: Boolean(tu.connected),
      baseUrl: String(tu.baseUrl || "https://www.technischeunie.com/servlet/RedirectShoppingCart"),
      username: String(tu.username || ""),
      customerNumber: String(tu.customerNumber || ""),
      hookUrl: String(tu.hookUrl || ""),
      vendor: String(tu.vendor || "Technische_Unie"),
      unitType: String(tu.unitType || "EAN").toUpperCase() === "ISO" ? "ISO" : "EAN",
      includeGrossPrice:
        tu.includeGrossPrice === undefined ? true : Boolean(tu.includeGrossPrice),
      includeDisposalContribution:
        tu.includeDisposalContribution === undefined
          ? true
          : Boolean(tu.includeDisposalContribution),
      includeGtin: tu.includeGtin === undefined ? true : Boolean(tu.includeGtin),
      includeVatPct: tu.includeVatPct === undefined ? true : Boolean(tu.includeVatPct),
      includeLeadTime:
        tu.includeLeadTime === undefined ? true : Boolean(tu.includeLeadTime),
      hasPassword: Boolean(tu.hasPassword),
    },
  };
}

function normalizeCompletionRequirements(value) {
  const source = value || {};
  return {
    requireExecutionLog:
      source.requireExecutionLog === undefined ? true : Boolean(source.requireExecutionLog),
    requireHours: source.requireHours === undefined ? true : Boolean(source.requireHours),
    requireMaterials:
      source.requireMaterials === undefined ? false : Boolean(source.requireMaterials),
    requireEvidence:
      source.requireEvidence === undefined ? true : Boolean(source.requireEvidence),
  };
}

function defaultIntegrationSettings() {
  return normalizeIntegrationSettings({});
}

function normalizeServiceShifts(value) {
  const shifts = Array.isArray(value) ? value : [];
  if (shifts.length === 0) return defaultServiceShifts();
  return shifts.map((item, index) => ({
    key: String(item?.key || `dienst-${index + 1}`),
    label: String(item?.label || ""),
    color: String(item?.color || "#0f172a"),
    startTime: String(item?.startTime || "08:00"),
    endTime: String(item?.endTime || "16:00"),
    active: item?.active !== false,
  }));
}

function defaultServiceShifts() {
  return [
    { key: "ochtend", label: "Ochtend", color: "#f59e0b", startTime: "06:00", endTime: "14:00", active: true },
    { key: "middag", label: "Middag", color: "#0ea5e9", startTime: "14:00", endTime: "22:00", active: true },
    { key: "nacht", label: "Nacht", color: "#334155", startTime: "22:00", endTime: "06:00", active: true },
  ];
}

function normalizeWorkorderTypes(value) {
  const items = Array.isArray(value) ? value : [];
  if (items.length === 0) return defaultWorkorderTypes();
  return items
    .map((item, index) => ({
    key: String(item?.key || `type_${index + 1}`),
    name: String(item?.name || ""),
    description: String(item?.description || ""),
    purpose: String(item?.purpose || ""),
    origin: String(item?.origin || "").trim().toLowerCase() === "system" ? "system" : "tenant",
    systemKey: String(item?.systemKey || ""),
    sortOrder: Number.isFinite(Number(item?.sortOrder)) ? Number(item.sortOrder) : (index + 1) * 10,
    active: item?.active !== false,
    signatureRequired: Boolean(item?.signatureRequired),
    generateWorkReport:
      item?.generateWorkReport === undefined ? true : Boolean(item?.generateWorkReport),
    moneybirdSync: Boolean(item?.moneybirdSync),
    // Backward compat: oude key portalVisible mappen naar nieuwe publishToClientHub.
    publishToClientHub:
      item?.publishToClientHub === undefined
        ? Boolean(item?.portalVisible)
        : Boolean(item?.publishToClientHub),
    allowNacalculatie:
      item?.allowNacalculatie === undefined ? true : Boolean(item?.allowNacalculatie),
    requireHours: deriveWorkorderTypeRequireHours(item),
    defaultHourTypeId: String(item?.defaultHourTypeId || ""),
    allowedHourTypeIds: Array.isArray(item?.allowedHourTypeIds)
      ? item.allowedHourTypeIds.map((id) => String(id || "")).filter(Boolean)
      : [],
    workflow: item?.workflow && typeof item?.workflow === "object" ? item.workflow : {},
    uiPolicy: normalizeWorkorderUiPolicy(item?.uiPolicy, {
      workorderKey: String(item?.key || item?.systemKey || item?.name || `type_${index + 1}`),
      signatureRequired: Boolean(item?.signatureRequired),
    }),
  }))
    .sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0));
}

const WORKORDER_UI_ROLE_OPTIONS = [
  { key: "monteur", label: "Monteur" },
  { key: "senior_monteur", label: "Senior monteur" },
  { key: "projectleider", label: "Projectleider" },
  { key: "tenantadmin", label: "Tenant admin" },
  { key: "klantportaal", label: "Klantportaal" },
];

const WORKORDER_UI_LAYOUT_OPTIONS = [
  { value: "standaard", label: "Standaard" },
  { value: "compact", label: "Compact" },
  { value: "uitgebreid", label: "Uitgebreid" },
];

const WORKORDER_UI_PRESET_OPTIONS = [
  {
    value: "auto_default",
    label: "Type-default",
    description: "Slimme standaard op basis van werkbontype.",
  },
  {
    value: "project_compact",
    label: "Project compact",
    description: "Monteur compact, projectleider uitgebreid.",
  },
  {
    value: "service_extended",
    label: "Service uitgebreid",
    description: "Monteur ziet uren, materiaal en extra tijd in uitgebreide weergave.",
  },
  {
    value: "consumer_extended",
    label: "Consument uitgebreid",
    description: "Uitgebreide serviceflow met zichtbaar klant-akkoord.",
  },
  {
    value: "readonly_monteur",
    label: "Monteur alleen-lezen",
    description: "Wel zichtbaarheid, geen mutaties door monteurrollen.",
  },
  {
    value: "custom",
    label: "Aangepast",
    description: "Handmatige combinatie van rolrechten.",
  },
];

const COMPACT_MONTEUR_TYPE_KEYS = new Set([
  "project",
  "offerte_project",
  "inspectie_controle",
  "opname_voorbereiding",
]);

const EXTENDED_MONTEUR_TYPE_KEYS = new Set([
  "service_storing",
  "regie",
  "wachttijd_geen_bon",
  "consumentenwerk",
  "intern_werk",
]);

function defaultUiRolePolicy(roleKey, { workorderKey = "", signatureRequired = false } = {}) {
  const normalizedKey = String(roleKey || "").trim().toLowerCase();
  const compactMonteurLayout = COMPACT_MONTEUR_TYPE_KEYS.has(
    String(workorderKey || "").trim().toLowerCase()
  )
    ? "compact"
    : EXTENDED_MONTEUR_TYPE_KEYS.has(String(workorderKey || "").trim().toLowerCase())
    ? "uitgebreid"
    : "standaard";
  const privileged = normalizedKey === "projectleider" || normalizedKey === "tenantadmin";
  const portal = normalizedKey === "klantportaal";

  return {
    layout: privileged ? "uitgebreid" : portal ? "compact" : compactMonteurLayout,
    showHours: !portal,
    editHours: !portal,
    showMaterials: true,
    editMaterials: !portal,
    showCosts: privileged,
    showPdf: true,
    showSignature: Boolean(signatureRequired) || privileged || portal,
    editSignature: !portal,
    showPhotos: true,
    editPhotos: !portal,
    showChecklist: true,
    editChecklist: !portal,
    requireSignature: Boolean(signatureRequired),
    requirePhotos: false,
    requireChecklist: false,
  };
}

function normalizeWorkorderUiPolicy(value, { workorderKey = "", signatureRequired = false } = {}) {
  const source = value && typeof value === "object" ? value : {};
  const rawRoles = source.roles && typeof source.roles === "object" ? source.roles : source;
  const roles = {};

  for (const role of WORKORDER_UI_ROLE_OPTIONS) {
    const defaults = defaultUiRolePolicy(role.key, { workorderKey, signatureRequired });
    const current = rawRoles?.[role.key] && typeof rawRoles[role.key] === "object" ? rawRoles[role.key] : {};
    roles[role.key] = {
      layout: String(current.layout || defaults.layout),
      showHours: current.showHours === undefined ? defaults.showHours : Boolean(current.showHours),
      editHours: current.editHours === undefined ? defaults.editHours : Boolean(current.editHours),
      showMaterials:
        current.showMaterials === undefined ? defaults.showMaterials : Boolean(current.showMaterials),
      editMaterials:
        current.editMaterials === undefined ? defaults.editMaterials : Boolean(current.editMaterials),
      showCosts: current.showCosts === undefined ? defaults.showCosts : Boolean(current.showCosts),
      showPdf: current.showPdf === undefined ? defaults.showPdf : Boolean(current.showPdf),
      showSignature:
        current.showSignature === undefined ? defaults.showSignature : Boolean(current.showSignature),
      editSignature:
        current.editSignature === undefined ? defaults.editSignature : Boolean(current.editSignature),
      showPhotos: current.showPhotos === undefined ? defaults.showPhotos : Boolean(current.showPhotos),
      editPhotos: current.editPhotos === undefined ? defaults.editPhotos : Boolean(current.editPhotos),
      showChecklist:
        current.showChecklist === undefined ? defaults.showChecklist : Boolean(current.showChecklist),
      editChecklist:
        current.editChecklist === undefined ? defaults.editChecklist : Boolean(current.editChecklist),
      requireSignature:
        current.requireSignature === undefined
          ? defaults.requireSignature
          : Boolean(current.requireSignature),
      requirePhotos:
        current.requirePhotos === undefined ? defaults.requirePhotos : Boolean(current.requirePhotos),
      requireChecklist:
        current.requireChecklist === undefined
          ? defaults.requireChecklist
          : Boolean(current.requireChecklist),
    };
  }

  return { roles };
}

function cloneUiPolicyRoles(policy = {}) {
  const roles = policy?.roles && typeof policy.roles === "object" ? policy.roles : {};
  return Object.fromEntries(
    Object.entries(roles).map(([roleKey, rolePolicy]) => [roleKey, { ...(rolePolicy || {}) }])
  );
}

function createWorkorderUiPreset(
  presetKey,
  { workorderKey = "", signatureRequired = false } = {}
) {
  const base = normalizeWorkorderUiPolicy({}, { workorderKey, signatureRequired });
  const roles = cloneUiPolicyRoles(base);
  const monteurRoles = ["monteur", "senior_monteur"];

  if (presetKey === "project_compact") {
    for (const roleKey of monteurRoles) {
      roles[roleKey] = {
        ...roles[roleKey],
        layout: "compact",
        showHours: false,
        editHours: false,
        showMaterials: true,
        editMaterials: true,
        showSignature: Boolean(signatureRequired),
        editSignature: Boolean(signatureRequired),
        showPhotos: true,
        editPhotos: true,
      };
    }
    roles.projectleider = { ...roles.projectleider, layout: "uitgebreid", showCosts: true };
    roles.tenantadmin = { ...roles.tenantadmin, layout: "uitgebreid", showCosts: true };
    roles.klantportaal = {
      ...roles.klantportaal,
      layout: "compact",
      showHours: false,
      editHours: false,
      showMaterials: true,
      editMaterials: false,
      showSignature: Boolean(signatureRequired),
      editSignature: false,
    };
    return { roles };
  }

  if (presetKey === "service_extended") {
    for (const roleKey of monteurRoles) {
      roles[roleKey] = {
        ...roles[roleKey],
        layout: "uitgebreid",
        showHours: true,
        editHours: true,
        showMaterials: true,
        editMaterials: true,
        showSignature: Boolean(signatureRequired),
        editSignature: true,
        showPhotos: true,
        editPhotos: true,
      };
    }
    roles.projectleider = { ...roles.projectleider, layout: "uitgebreid", showCosts: true };
    roles.tenantadmin = { ...roles.tenantadmin, layout: "uitgebreid", showCosts: true };
    return { roles };
  }

  if (presetKey === "consumer_extended") {
    for (const roleKey of monteurRoles) {
      roles[roleKey] = {
        ...roles[roleKey],
        layout: "uitgebreid",
        showHours: true,
        editHours: true,
        showMaterials: true,
        editMaterials: true,
        showSignature: true,
        editSignature: true,
        requireSignature: Boolean(signatureRequired),
        showPhotos: true,
        editPhotos: true,
      };
    }
    roles.projectleider = { ...roles.projectleider, layout: "uitgebreid", showCosts: true };
    roles.tenantadmin = { ...roles.tenantadmin, layout: "uitgebreid", showCosts: true };
    roles.klantportaal = {
      ...roles.klantportaal,
      layout: "compact",
      showSignature: true,
      editSignature: false,
      requireSignature: Boolean(signatureRequired),
    };
    return { roles };
  }

  if (presetKey === "readonly_monteur") {
    for (const roleKey of monteurRoles) {
      roles[roleKey] = {
        ...roles[roleKey],
        showHours: true,
        editHours: false,
        showMaterials: true,
        editMaterials: false,
        showSignature: Boolean(signatureRequired) || roles[roleKey].showSignature === true,
        editSignature: false,
        showPhotos: true,
        editPhotos: false,
        showChecklist: true,
        editChecklist: false,
      };
    }
    return { roles };
  }

  return base;
}

function inferWorkorderUiPresetKey(
  uiPolicy,
  { workorderKey = "", signatureRequired = false } = {}
) {
  const normalizedCurrent = normalizeWorkorderUiPolicy(uiPolicy, {
    workorderKey,
    signatureRequired,
  });
  for (const option of WORKORDER_UI_PRESET_OPTIONS) {
    if (option.value === "custom") continue;
    const preset = createWorkorderUiPreset(option.value, {
      workorderKey,
      signatureRequired,
    });
    if (JSON.stringify(preset) === JSON.stringify(normalizedCurrent)) {
      return option.value;
    }
  }
  return "custom";
}

function defaultWorkorderTypes() {
  return [
    {
      key: "regie",
      name: "Regie",
      description: "Ad-hoc of nacalculatie werkzaamheden.",
      purpose: "Flexibele uitvoering met nacalculatie.",
      origin: "system",
      systemKey: "regie",
      sortOrder: 10,
      active: true,
      signatureRequired: false,
      generateWorkReport: true,
      moneybirdSync: false,
      publishToClientHub: false,
      allowNacalculatie: true,
      requireHours: true,
      defaultHourTypeId: "",
      allowedHourTypeIds: [],
      workflow: { requireHours: true },
      uiPolicy: normalizeWorkorderUiPolicy({}, { workorderKey: "regie", signatureRequired: false }),
    },
    {
      key: "project",
      name: "Project",
      description: "Uitvoering binnen projectscope en planning.",
      purpose: "Projectmatige uitvoering met focus op voortgang.",
      origin: "system",
      systemKey: "project",
      sortOrder: 20,
      active: true,
      signatureRequired: true,
      generateWorkReport: true,
      moneybirdSync: false,
      publishToClientHub: true,
      allowNacalculatie: false,
      requireHours: false,
      defaultHourTypeId: "",
      allowedHourTypeIds: [],
      workflow: { requireHours: false },
      uiPolicy: normalizeWorkorderUiPolicy({}, { workorderKey: "project", signatureRequired: true }),
    },
    {
      key: "service_storing",
      name: "Service/Storing",
      description: "Servicebezoeken, storingen en herstelwerk.",
      purpose: "Snelle afhandeling van meldingen.",
      origin: "system",
      systemKey: "service_storing",
      sortOrder: 30,
      active: true,
      signatureRequired: false,
      generateWorkReport: true,
      moneybirdSync: true,
      publishToClientHub: true,
      allowNacalculatie: true,
      requireHours: true,
      defaultHourTypeId: "",
      allowedHourTypeIds: [],
      workflow: { requireHours: true },
      uiPolicy: normalizeWorkorderUiPolicy(
        {},
        { workorderKey: "service_storing", signatureRequired: false }
      ),
    },
    {
      key: "inspectie_controle",
      name: "Inspectie/Controle",
      description: "Inspecties en periodieke controles.",
      purpose: "Compliance en controlewerk.",
      origin: "system",
      systemKey: "inspectie_controle",
      sortOrder: 40,
      active: true,
      signatureRequired: true,
      generateWorkReport: true,
      moneybirdSync: false,
      publishToClientHub: true,
      allowNacalculatie: false,
      requireHours: false,
      defaultHourTypeId: "",
      allowedHourTypeIds: [],
      workflow: { requireHours: false },
      uiPolicy: normalizeWorkorderUiPolicy(
        {},
        { workorderKey: "inspectie_controle", signatureRequired: true }
      ),
    },
    {
      key: "opname_voorbereiding",
      name: "Opname/Voorbereiding",
      description: "Inventarisatie en voorbereiding op uitvoering.",
      purpose: "Voorbereiding van offerte of uitvoering.",
      origin: "system",
      systemKey: "opname_voorbereiding",
      sortOrder: 50,
      active: true,
      signatureRequired: false,
      generateWorkReport: false,
      moneybirdSync: false,
      publishToClientHub: false,
      allowNacalculatie: false,
      requireHours: false,
      defaultHourTypeId: "",
      allowedHourTypeIds: [],
      workflow: { requireHours: false },
      uiPolicy: normalizeWorkorderUiPolicy(
        {},
        { workorderKey: "opname_voorbereiding", signatureRequired: false }
      ),
    },
  ];
}

function deriveWorkorderTypeRequireHours(item) {
  const workflowRequireHours =
    item?.workflow && typeof item.workflow === "object"
      ? item.workflow.requireHours
      : undefined;
  if (workflowRequireHours === undefined) {
    return item?.allowNacalculatie !== false;
  }
  return Boolean(workflowRequireHours);
}

function normalizeHourTypes(value) {
  const items = Array.isArray(value) ? value : [];
  return items.map((item) => ({
    _id: String(item?._id || ""),
    name: String(item?.name || item?.code || "Uurtype"),
  }));
}

function defaultRbacRoleForm() {
  return {
    name: "",
    description: "",
    permissionKeys: [],
  };
}

function normalizeRbacPermissionDisplay(permission) {
  const key = String(permission?.key || "").trim();
  const rawLabel = String(permission?.label || "").trim();
  const rawGroup = String(permission?.group || "Overig").trim() || "Overig";

  if (key.startsWith("mandagstaten.")) {
    return {
      _id: String(permission?._id || ""),
      key,
      label: rawLabel || key,
      group: "Mandagstaten",
      system: Boolean(permission?.system),
    };
  }

  return {
    _id: String(permission?._id || ""),
    key,
    label: rawLabel || key,
    group: rawGroup,
    system: Boolean(permission?.system),
  };
}

function normalizeRbacConfiguratorData(value) {
  const source = value || {};
  const permissions = Array.isArray(source.permissions) ? source.permissions : [];
  const roles = Array.isArray(source.roles) ? source.roles : [];
  const users = Array.isArray(source.users) ? source.users : [];
  return {
    permissions: permissions.map((permission) => normalizeRbacPermissionDisplay(permission)),
    roles: roles.map((role) => ({
      _id: String(role?._id || ""),
      name: String(role?.name || "").trim(),
      description: String(role?.description || "").trim(),
      system: Boolean(role?.system),
      permissions: Array.isArray(role?.permissions)
        ? role.permissions.map((permission) => normalizeRbacPermissionDisplay(permission))
        : [],
    })),
    users: users.map((item) => ({
      _id: String(item?._id || ""),
      naam: String(item?.naam || "").trim(),
      email: String(item?.email || "").trim(),
      roles: Array.isArray(item?.roles)
        ? item.roles.map((role) => ({
            _id: String(role?._id || ""),
            name: String(role?.name || "").trim(),
            system: Boolean(role?.system),
          }))
        : [],
    })),
  };
}

function buildRbacUserRoleDrafts(users) {
  const rows = Array.isArray(users) ? users : [];
  const next = {};
  for (const user of rows) {
    const userId = String(user?._id || "");
    if (!userId) continue;
    next[userId] = Array.isArray(user?.roles)
      ? user.roles.map((role) => String(role?._id || "")).filter(Boolean)
      : [];
  }
  return next;
}

function groupRbacPermissions(permissions) {
  const items = Array.isArray(permissions) ? permissions : [];
  const grouped = new Map();
  for (const permission of items) {
    const group = String(permission?.group || "Overig").trim() || "Overig";
    if (!grouped.has(group)) grouped.set(group, []);
    grouped.get(group).push(permission);
  }
  return Array.from(grouped.entries())
    .map(([group, groupPermissions]) => ({
      group,
      permissions: groupPermissions.sort((a, b) =>
        String(a?.label || a?.key || "").localeCompare(String(b?.label || b?.key || ""), "nl")
      ),
    }))
    .sort((a, b) => a.group.localeCompare(b.group, "nl"));
}

function filterRbacPermissionGroups(groups, searchTerm) {
  const term = String(searchTerm || "").trim().toLocaleLowerCase("nl-NL");
  if (!term) return groups;

  return groups
    .map((group) => ({
      ...group,
      permissions: group.permissions.filter((permission) => {
        const haystack = `${permission?.label || ""} ${permission?.key || ""}`.toLocaleLowerCase("nl-NL");
        return haystack.includes(term);
      }),
    }))
    .filter((group) => group.permissions.length > 0);
}

function getRoleNames(user) {
  const roleSet = new Set();
  if (user?.rol) roleSet.add(String(user.rol).trim().toLowerCase());
  if (user?.role) roleSet.add(String(user.role).trim().toLowerCase());
  for (const role of user?.roles || []) {
    if (!role) continue;
    if (typeof role === "string") {
      roleSet.add(role.trim().toLowerCase());
      continue;
    }
    if (role?.name || role?.naam) {
      roleSet.add(String(role.name || role.naam).trim().toLowerCase());
    }
  }
  return Array.from(roleSet);
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}
